import { useState, useContext, createContext, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { usePostMessages } from "../hooks/usePostMessages";
import { useAuth } from "../hooks/useAuth";
import {
  arrayBuffersToDataUrl,
  base64ToSingleArrayBuffer,
  convertArrayBuffersToMP3,
  convertBase64ToMP3,
  processCompletedAudioTransfer,
} from "../utils/utils";
import { useChats } from "./useChats";
import { subscribe, unsubscribe } from "../utils/events";
import { useIndexDb } from "./useIndexDb";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { getDeviceId, getDeviceLabel } from "../utils/deviceId";
import { isNonIdempotentRecording } from "../utils/recordingIdempotency";
import { RECORDING_SAVE_DEFERRED_OFFLINE } from "../utils/recordingSaveError";
import { encounterUuidOf, isIdentityRefusal } from "../utils/recordingIdentityGuard";
import { rememberIdentityRefusal } from "../utils/nativeIdentityRefusals";
import { dismissRecordingProgressToast, linkRecordingProgressToast, recordingProgressToastId } from "../utils/recordingToast";
import { isSegmentedRecordingEnabled } from "../utils/segmentedRecordingFlag";
import { estimateRecordingHeadroom, isHeadroomLow } from "../utils/storageHeadroom";
import { writeHeartbeat, clearHeartbeat, setActiveRecording, clearActiveRecording } from "../utils/recordingHeartbeat";
import { markCaptureInterrupted } from "../utils/recordingInterrupted";
import { acquireWebRecordingOwnership } from "../utils/webRecordingOwnership";
import { inspectWebRecordingCustody, recordWebRecordingSaved } from "../utils/webRecordingCustody";
import { currentOrgId } from "../utils/currentOrgId";
import { logActivity } from "../utils/activityLog";
import {
  writeNativeRecordingMarker,
  readNativeRecordingMarker,
  clearNativeRecordingMarker,
  peekNativeRecordingMarker,
  markNativeRecordingMarkerStopped,
  bindNativeRecordingId,
  markNativeLocalRecordingsChecked,
  markNativeMarkerDeadEndReported,
  MARKER_GRACE_MS,
  LOCAL_RECORDINGS_CHECK_MAX_AGE_MS,
} from "../utils/nativeRecordingMarker";
import * as Sentry from "@sentry/react";

// Length of each independent audio segment. Restarting MediaRecorder on this
// cadence yields a COMPLETE, self-contained file per segment (timeslice
// fragments share one header and are not independently decodable), so a lost
// segment costs ~one interval of audio, not the whole recording.
const SEGMENT_MS = 10000;

// Minimum recording length. A stop (or pause) inside this window is ignored and
// the stop controls render disabled — an accidental double-tap otherwise mints
// a junk capture (the 2026-07-21 incident's second recording was 1.6s) whose
// empty transcript overwrites the provider's chance to notice the real loss.
const MIN_RECORDING_MS = 3000;

// If no terminal signal (completion SSE, watchdog, or an explicit failure-clear)
// fires, release the record lock after the watchdog ceiling so the button can
// never strand. Matches useTranscriptionWatchdog's 300s ceiling.
const TRANSCRIBE_LOCK_BACKSTOP_MS = 300000;

// How long the lost-recording rescue waits for native to hand audio back after
// sendStopRecording before conceding ("check Local Recordings").
const LOST_RECORDING_RESCUE_TIMEOUT_MS = 10000;
const WEB_MIC_ACQUISITION_TIMEOUT_MS = 15000;

// Identifiers, timestamps and flags only. The marker's other fields (phase,
// anything added later) stay out: this channel must never carry patient data.
const reportNativeMarker = (message, marker, lostRecordingState, handoff) => {
  const now = Date.now();
  Sentry.captureMessage(message, {
    level: "warning",
    tags: { component: "useRecorder", issue: "native-lost-recording", recordingUuid: marker.recordingUuid },
    contexts: {
      nativeRecording: {
        recordingUuid: marker.recordingUuid,
        chatId: marker.chatId,
        startedAt: marker.startedAt,
        stoppedAt: marker.stoppedAt,
        minutesSinceStart: marker.startedAt ? Math.round((now - marker.startedAt) / 60000) : null,
        lostRecordingState,
        stopped: !!marker.stoppedAt,
        startFailed: !!marker.startFailed,
        recoveryCandidate: !!marker.recoveryCandidate,
        nativeRecordingIdBound: marker.nativeRecordingId != null,
        localRecordingsCheckedAt: marker.localRecordingsCheckedAt,
        deadEndReportedAt: marker.deadEndReportedAt,
        handoffAttached: !!handoff,
        handoffTimedOut: !!handoff?.timedOut,
      },
    },
  });
};
const stopStream = (stream) => stream?.getTracks?.().forEach((track) => track.stop());

// A provider can unmount without its document closing (for example, during
// navigation). Keep its unsaved bytes reachable so the next provider can retry
// the same session while its Web Lock remains held. Separate documents still
// coordinate exclusively through Web Locks and the persistent journal.
const detachedWebSessions = new WeakMap();

// Segmented/streaming capture is the default web recording path. The kill-switch
// `ff_segmentedRecording="0"` forces the proven single-blob path per-browser with
// no redeploy (see isSegmentedRecordingEnabled). If localStorage is unavailable,
// default ON.
const segmentedRecordingEnabled = () => {
  try {
    return isSegmentedRecordingEnabled(localStorage.getItem("ff_segmentedRecording"));
  } catch (_) {
    return true;
  }
};

// A segment whose LOCAL write failed has exactly one copy left: the server's A2
// backstop. That copy stops being best-effort at that moment, so it is retried on
// this budget instead of being fired and forgotten. Three attempts across ~8s ride
// out a momentary drop without holding the recording open for long.
const RESCUE_ATTEMPTS = 3;
const RESCUE_BACKOFF_MS = [2000, 6000];
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Resolves true once the server holds these bytes, false when every attempt failed
// (the only genuinely unrecoverable case — no copy of that audio exists anywhere).
// Never rejects: a rescue failure must not throw into the recording flow.
const rescueSegmentToServer = async ({
  recordingId,
  chatId,
  index,
  dataUrl,
  isFinal,
  total,
  language,
  firstAttempt,
  canUpload,
}) => {
  if (!dataUrl) return false; // an empty final segment carries no audio to rescue
  try {
    if (firstAttempt && (await firstAttempt)) return true;
  } catch (_) {
    // firstAttempt is already failure-mapped; guard anyway.
  }
  for (let attempt = 1; attempt < RESCUE_ATTEMPTS; attempt++) {
    await delay(RESCUE_BACKOFF_MS[attempt - 1] ?? RESCUE_BACKOFF_MS[RESCUE_BACKOFF_MS.length - 1]);
    if (canUpload && !canUpload()) return false;
    try {
      await api.recordingSegment(recordingId, chatId, index, dataUrl, isFinal, total, language);
      return true;
    } catch (_) {
      /* keep trying */
    }
  }
  return false;
};

// A local write that failed can simply be tried again: the audio is still in
// memory at that moment, and the commonest causes (a transaction aborted under
// memory pressure, a momentary quota spike that the reclaim below then clears) are
// transient. Above this target, evict only entries whose server copy succeeded;
// an in-flight or failed rescue is never permission to drop the only audio copy.
// 12 entries is ~2 minutes of audio at ~80 KB per base64 segment, i.e. ~1 MB.
const MAX_PENDING_WRITES = 12;

// A 10s segment of Opus speech at 48 kbps is ~50-80 KB — measured live at 44-59 KB
// on 2026-08-19. A WebM/MP4 container header with NO audio frames is a few hundred
// bytes, and that is what a recorder writes when its track has stopped delivering
// audio: files that look like segments, decode to nothing, and are only discovered
// hours later when assembly finds it has no audio at all (LUCASHEALTH-S5/RQ: 1.6K
// and 1.4K events). 1 KB sits two orders of magnitude below anything real — even a
// single second of speech is ~6 KB — so this can only catch a segment that carries
// no audio whatsoever.
const MIN_SEGMENT_BYTES = 1024;

// Bound on the native handoff's duplicate-message guard. A chunked transfer sends
// one message per chunk, so an unbounded set grows with every recording of the
// session; 500 is far more than the in-flight window a duplicate can arrive in.
const MAX_DEDUP_IDS = 500;

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// Since the app now has reached a level of recording stability, rural/offline
// recording mode has been disabled to reduce complexity
// re-enable rural/offline recording mode
export const RURAL_MODE_ENABLED = false;

export const RecorderContext = createContext();

function useRecorderProvider() {
  const { t } = useTranslation();
  const mediaRecorder = useRef();
  const streamRef = useRef();
  const intervalRef = useRef();
  const startTimeRef = useRef();
  // Wall-clock stamp of when THIS capture started. Deliberately separate from
  // startTimeRef, which is back-dated by the cumulative prior duration for
  // resumed/phase recordings and therefore useless for a min-duration gate.
  const recordStartedAtRef = useRef(null);
  const analyserRef = useRef();
  const audioContextRef = useRef();
  const animationFrameRef = useRef();
  const silenceTimeoutRef = useRef();

  const lastProcessedMessage = useRef(new Set());
  const audioChunkTransfers = useRef({});

  const [recording, setRecording] = useState(false);
  const [recordingVariable, setRecordingVariable] = useState();
  const [ruralMode, setRuralMode] = useState(false);
  const [quickstart, setQuickstart] = useState();
  const [time, setTime] = useState(false);
  const [recordingElapsed, setRecordingElapsed] = useState(0);
  const [startingRecording, setStartingRecording] = useState(false);
  const [devices, setDevices] = useState([]);
  const [currentDevice, setCurrentDevice] = useState();
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [showNoAudioWarning, setShowNoAudioWarning] = useState(false);
  const [showModalRecordingPermission, setShowModalRecordingPermission] = useState(false);
  // A native recording orphaned by a webview reset (marker present on mount /
  // record-press but nothing recording). Drives ModalLostRecording.
  const [lostRecording, setLostRecording] = useState(null);
  const [lostRecordingState, setLostRecordingState] = useState("idle"); // idle | rescuing | notRecovered
  const rescueTimerRef = useRef(null);
  // A long native transcode may outlive the marker's orphan-detection grace.
  // Within this webview, wait for its handoff rather than issuing another stop
  // or replacing the native recorder while finalization is still running.
  const nativeHandoffRef = useRef(null);
  const nativeStartRef = useRef(null);
  const pendingNativeResumeRef = useRef(null);
  const webSessionRef = useRef(null);
  const mountedRef = useRef(true);
  const webDeviceSwapRef = useRef(false);
  const reviewedWebRecordingRef = useRef(null);
  const [webRecordingRecovery, setWebRecordingRecovery] = useState(null);

  const { sendStartRecording, sendStopRecording, sendRecordingUpdate } = usePostMessages();
  const { auth, isMobileApp, isServerAvailable, updateProfile } = useAuth();
  const recordingUserRef = useRef(auth?.user);
  recordingUserRef.current = auth?.user;
  const { parseRecording, parseBlob, updateChatValue, chats, chat: openChat } = useChats();
  // The patient and encounter UUID a recording was made for, captured at record time
  // and stored on its queue rows: encounter ids are recycled after hard deletes, so the
  // id alone cannot say whose audio a row holds once it is drained later.
  const patientSourcesRef = useRef({ chats, openChat });
  patientSourcesRef.current = { chats, openChat };
  const capturePatient = (chatId) => {
    const { chats: list, openChat: open } = patientSourcesRef.current || {};
    const found =
      (open && open.id === chatId ? open : null) || (Array.isArray(list) ? list.find((c) => c && c.id === chatId) : null);
    if (!found) return {};
    const p = found.user || found.patient;
    const label = p ? `${p.firstName || ""} ${p.lastName || ""}`.trim() : "";
    return {
      patientUserId: found.userId || found.user?.id || found.patient?.id || undefined,
      patientLabel: label || undefined,
      // The encounter's never-recycled UUID: the exact identity the server and the drain
      // check. Absent on encounters created before it existed (judged on time + patient).
      encounterUuid: encounterUuidOf(found) || undefined,
    };
  };
  // A restored handoff can wait across reconnects and authentication refreshes.
  // Its listener must use the current save function and server availability.
  const nativeSaveRef = useRef({ isServerAvailable, parseRecording });
  nativeSaveRef.current = { isServerAvailable, parseRecording };
  const { addRecording, addRecordingSegment, setShowRecordingsModal } = useIndexDb();

  const segmentTimerRef = useRef();
  // Per-recording capture context. Each recording gets a FRESH object holding its
  // own recordingId, segment-index counter, ordering chain, and active flag, and
  // every MediaRecorder's onstop closes over ITS recording's context. This is what
  // makes a fast pause→record safe: a previous recording's async final-flush can
  // no longer read the new recording's id/index (the old bug mis-tagged a segment
  // to the next recording and dropped one entirely). The index counter lives on
  // the context, not a shared ref, so it can never be advanced across recordings.
  const activeCaptureRef = useRef(null);

  const chatIdRef = useRef();
  const phaseRef = useRef();
  const extraDataRef = useRef();
  const pendingRecordActionRef = useRef(null);
  const [showModalHealthCheck, setShowModalHealthCheck] = useState(false);
  const callbackRef = useRef();
  const hasSpoken = useRef();
  // chatId -> backstop timeout id. Keyed per chat so an earlier recording's
  // backstop can never clear a later recording's lock.
  const lockTimersRef = useRef({});
  // The backstop fires up to TRANSCRIBE_LOCK_BACKSTOP_MS after a recording stops,
  // long after the arm-time render. `updateChatValue` closes over the chats
  // snapshot of the render that created it, so calling the arm-time copy would
  // merge {transcribing:false} onto a stale chat and revert it (dropping the
  // landed transcript / edits made since). Fire the LATEST copy via this ref so
  // the deferred write targets current state -- a true no-op once already false.
  const updateChatValueRef = useRef(updateChatValue);
  updateChatValueRef.current = updateChatValue;
  // Fresh chats snapshot for the startRecording chokepoint guard below — same
  // staleness rationale as updateChatValueRef (start can be triggered from
  // handlers that closed over an older render).
  const chatsRef = useRef(chats);
  chatsRef.current = chats;
  // Synchronous re-entrancy gate: true from the instant a start is accepted
  // until the capture stops (or the start terminally fails). State mirrors
  // (recording/startingRecording) update a render late, so a double-tap in the
  // same tick — or a tap on an unguarded entry point while getUserMedia's
  // permission prompt is pending — would otherwise launch a second capture
  // that overwrites mediaRecorder.current/chatIdRef and leaks the first
  // interval. Applies to ALL flows, callback included: two concurrent
  // captures are never valid on the shared recorder refs.
  const captureGateRef = useRef(false);
  // Who is capturing, stamped when the recording ARMS rather than read live at
  // write time. A session that lapses mid-encounter used to make every remaining
  // segment fail addRecordingSegment's userId guard — and a userId-less row is
  // invisible to the drain, so the audio was queued nowhere and uploaded by nobody.
  // The identity that started the capture is the correct owner of its audio for the
  // whole capture, so it is captured once, here.
  const captureIdentityRef = useRef(null);
  // One low-storage warning per session: the condition is a property of the device,
  // not of this recording, so repeating it every start is nagging, not informing.
  const lowStorageWarnedRef = useRef(false);
  // Every browser permission request owns a token. A stop, unmount, or later
  // start invalidates it so a delayed getUserMedia resolution is stopped rather
  // than adopted into a new recording session.
  const webStartTokenRef = useRef(0);

  // Lifetime cleanup reads current refs; do not stop an active recording on each
  // render. Final audio persistence intentionally continues after unmount.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const session = webSessionRef.current;
      if (session) {
        session.ownership.update("interrupted");
        markCaptureInterrupted(session.recordingUuid);
        if (session.started) {
          detachedWebSessions.set(session.lockManager, session);
          stopRecordingWeb();
        } else releaseWebSession(session, true);
      }
      Object.values(lockTimersRef.current).forEach((id) => clearTimeout(id));
      if (rescueTimerRef.current) clearTimeout(rescueTimerRef.current);
      nativeHandoffRef.current?.cleanup?.();
      nativeHandoffRef.current = null;
      webStartTokenRef.current += 1;
      clearInterval(intervalRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        try {
          audioContextRef.current.close();
        } catch (e) {
          console.warn("Audio context close error:", e);
        }
      }
      stopStream(streamRef.current);
      intervalRef.current = null;
      streamRef.current = null;
      animationFrameRef.current = null;
      silenceTimeoutRef.current = null;
      audioContextRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const releaseWebSession = async (session, safelyStored) => {
    if (!session || session.releasing) return;
    if (!safelyStored) {
      session.saveFailed = true;
      session.ownership.update("interrupted");
      if (mountedRef.current && webSessionRef.current === session) {
        setWebRecordingRecovery({
          journal: session.ownership.journal,
          custody: { status: "unsaved" },
          live: true,
          retrying: false,
        });
      }
      if (!session.segmentContext?.storageFailureToasted) {
        toast.error(t("hooks.useRecorder.errorSavingRecordingWeb"), { toastId: "web-recording-unsaved" });
      }
      return;
    }
    session.releasing = true;
    await session.ownership.release({ clearJournal: true });
    session.released = true;
    if (detachedWebSessions.get(session.lockManager) === session) detachedWebSessions.delete(session.lockManager);
    if (webSessionRef.current === session) webSessionRef.current = null;
    if (mountedRef.current)
      setWebRecordingRecovery((previous) => (previous?.journal.recordingUuid === session.recordingUuid ? null : previous));
  };

  const retryWebRecordingSave = async () => {
    const session = webSessionRef.current;
    if (
      !session?.saveFailed ||
      session.retrying ||
      !session.retrySave ||
      session.userId !== recordingUserRef.current?.id ||
      session.orgId !== currentOrgId()
    )
      return;
    session.retrying = true;
    setWebRecordingRecovery({ journal: session.ownership.journal, custody: { status: "unsaved" }, live: true, retrying: true });
    let stored = false;
    try {
      stored = await session.retrySave();
    } catch (error) {
      console.error("Recording save retry failed", error);
    } finally {
      session.retrying = false;
      await releaseWebSession(session, stored);
    }
  };

  const acquireWebSession = async (restoreScroll, token) => {
    const session = {
      recordingUuid: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : uuidv4(),
      userId: captureUserId(),
      orgId: currentOrgId(),
      chatId: chatIdRef.current,
      ...capturePatient(chatIdRef.current),
      phase: phaseRef.current || "",
      extraData: extraDataRef.current,
      callback: callbackRef.current,
      startedAt: recordStartedAtRef.current,
      language: captureLanguage(),
      started: false,
      lockManager: navigator.locks,
    };
    let pendingCustody;
    const result = await acquireWebRecordingOwnership(session, {
      recoverPending: async (journal) => {
        // An abandoned permission request captured no audio. We persist the
        // recording state BEFORE calling MediaRecorder.start, so only starting
        // is safe to retire without an audio custody check.
        if (journal.state === "starting") return true;
        pendingCustody = await inspectWebRecordingCustody({
          recordingId: journal.recordingUuid,
          userId: session.userId,
          orgId: session.orgId,
        });
        if (pendingCustody.status === "complete") return true;
        markCaptureInterrupted(journal.recordingUuid);
        return pendingCustody.status !== "unknown" && reviewedWebRecordingRef.current === journal.recordingUuid;
      },
    });
    if (
      !mountedRef.current ||
      token !== webStartTokenRef.current ||
      session.userId !== recordingUserRef.current?.id ||
      session.orgId !== currentOrgId()
    ) {
      if (result.status === "acquired") await result.handle.release({ clearJournal: true });
      if (mountedRef.current && token === webStartTokenRef.current) {
        captureGateRef.current = false;
        setStartingRecording(false);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      reportBlockedStart("session-superseded", session.chatId);
      return;
    }
    if (result.status !== "acquired") {
      captureGateRef.current = false;
      setStartingRecording(false);
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      reportBlockedStart("ownership-" + result.status, session.chatId);
      if (result.status === "pending" && result.journal.userId === session.userId && result.journal.orgId === session.orgId) {
        setWebRecordingRecovery({ journal: result.journal, custody: pendingCustody || { status: "unknown" } });
      } else {
        const key =
          result.status === "busy"
            ? "webRecordingBusy"
            : result.status === "pending"
              ? "webRecordingOtherAccount"
              : "webRecordingUnavailable";
        toast.info(t(`hooks.useRecorder.${key}`), { toastId: "web-recording-blocked" });
      }
      return;
    }
    reviewedWebRecordingRef.current = null;
    setWebRecordingRecovery(null);
    session.ownership = result.handle;
    webSessionRef.current = session;
    startRecordingWeb(restoreScroll, token);
  };

  const dismissWebRecordingRecovery = () => setWebRecordingRecovery(null);
  const reviewWebRecordingRecovery = () => {
    setShowRecordingsModal?.(true);
    setWebRecordingRecovery(null);
  };
  const continueAfterWebRecordingReview = () => {
    if (
      !webRecordingRecovery ||
      webRecordingRecovery.live ||
      ["unknown", "unsaved"].includes(webRecordingRecovery.custody.status) ||
      captureGateRef.current
    )
      return;
    if (
      webRecordingRecovery.journal.userId !== recordingUserRef.current?.id ||
      webRecordingRecovery.journal.orgId !== currentOrgId()
    )
      return;
    reviewedWebRecordingRef.current = webRecordingRecovery.journal.recordingUuid;
    setWebRecordingRecovery(null);
  };

  const resumeNativeRecording = (startedAt) => {
    if (!Number.isFinite(startedAt) || startedAt <= 0) return;
    // The installed shell sends only startedAt and may still report its row
    // as recording during Stop. The local marker owns encounter identity.
    const marker = peekNativeRecordingMarker();
    const user = recordingUserRef.current;
    // Native answers isReady once, often before authentication has loaded.
    // Hold the notification until we can check the marker's owner.
    if (!user?.id) {
      pendingNativeResumeRef.current = startedAt;
      return;
    }
    if (marker?.userId === user.id && marker.stoppedAt) return;
    if (marker && marker.userId !== user?.id) return;
    if (marker && marker.userId === user?.id) {
      chatIdRef.current = marker.chatId;
      phaseRef.current = marker.phase;
      captureIdentityRef.current = {
        userId: marker.userId,
        language: user?.custom?.transcriptLanguage || "en",
      };
      setQuickstart(marker.chatId === "quick_start");
    }

    setRecording(true);
    setStartingRecording(false);
    setLostRecording(null);
    setLostRecordingState("idle");
    // A native capture is live (resume after a webview reset): hold the
    // re-entrancy gate so no path can start a second capture over it.
    captureGateRef.current = true;

    startTimeRef.current = startedAt;
    // Stamp the min-duration gate with the TRUE capture start (this is a
    // resume of an already-running native recording, not a fresh start) so
    // the 3s stop gate measures real elapsed recording time instead of
    // failing open on a missing stamp.
    recordStartedAtRef.current = startedAt;
    const elapsed = Math.floor((Date.now() - startedAt) / 1000);
    setTime(elapsed);
    setRecordingElapsed(elapsed);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      setRecordingElapsed(Math.floor((Date.now() - recordStartedAtRef.current) / 1000));
    }, 1000);
  };

  const releaseRecoveredNativeRecording = (marker) => {
    nativeHandoffRef.current?.cleanup?.();
    nativeHandoffRef.current = null;
    if (rescueTimerRef.current) clearTimeout(rescueTimerRef.current);
    rescueTimerRef.current = null;
    clearNativeRecordingMarker(marker.recordingUuid);
    // The upload may have been accepted while transcription is still running.
    // Its SSE/watchdog owns that lock; recovery releases only native custody.
    setLostRecording(null);
    setLostRecordingState("idle");
  };

  // Setting Message ID
  useEffect(() => {
    const gotRecordingInProgress = (event) => resumeNativeRecording(Number(event?.detail?.startedAt));

    const gotMicrophoneNotAvailable = () => {
      const start = nativeStartRef.current;
      const marker = peekNativeRecordingMarker();
      // Only a start issued by this provider can fail here. A late or duplicate
      // failure must not erase an orphan, another user's marker, or a handoff.
      if (!start || !captureGateRef.current || nativeHandoffRef.current) return;
      if (start.userId !== recordingUserRef.current?.id) return;
      if (marker && (marker.recordingUuid !== start.recordingUuid || marker.userId !== start.userId || marker.stoppedAt)) return;
      nativeStartRef.current = null;
      // The old shell also emits this after a failure to insert its recording
      // row, when hardware may already be capturing. Preserve the marker until
      // the user verifies that no audio was recorded, or recovery succeeds.
      const failedMarker = marker && { ...marker, startFailed: true };
      if (failedMarker) writeNativeRecordingMarker(failedMarker);
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      recordStartedAtRef.current = null;
      setRecordingElapsed(0);
      setTime(0);
      setRecording(false);
      setStartingRecording(false);
      // The persistent marker continues to guard starts after the UI stops.
      captureGateRef.current = false;
      setLostRecording(failedMarker);
      setLostRecordingState("idle");
    };

    const nativeRecordingRecovered = (event) => {
      const marker = peekNativeRecordingMarker();
      // Several recordings can share one encounter. Only a confirmed save of
      // this UUID can release its unresolved handoff after Local Recordings sync.
      if (!event?.detail?.recordingUuid || marker?.recordingUuid !== event.detail.recordingUuid) return;
      if (marker.userId !== recordingUserRef.current?.id || captureGateRef.current) return;
      releaseRecoveredNativeRecording(marker);
    };

    const nativeRecordingRecoveryNeedsConfirmation = (event) => {
      const marker = peekNativeRecordingMarker();
      if (!marker?.recoveryCandidate || marker.recordingUuid !== event?.detail?.recordingUuid) return;
      if (marker.userId !== recordingUserRef.current?.id || captureGateRef.current) return;
      if (nativeHandoffRef.current?.marker.recordingUuid === marker.recordingUuid) {
        nativeHandoffRef.current.marker = marker;
        nativeHandoffRef.current.timedOut = true;
      }
      setLostRecording(marker);
      setLostRecordingState("notRecovered");
    };

    const gotVolumeLevel = async (data) => {
      let level = data.detail.level;
      setVolumeLevel(level + 80);
    };

    subscribe("gotVolumeLevel", gotVolumeLevel);
    subscribe("gotRecordingInProgress", gotRecordingInProgress);
    subscribe("nativeRecordingRecovered", nativeRecordingRecovered);
    subscribe("nativeRecordingRecoveryNeedsConfirmation", nativeRecordingRecoveryNeedsConfirmation);
    subscribe("gotMicrophoneNotAvailable", gotMicrophoneNotAvailable);
    return () => {
      unsubscribe("gotVolumeLevel", gotVolumeLevel);
      unsubscribe("gotRecordingInProgress", gotRecordingInProgress);
      unsubscribe("nativeRecordingRecovered", nativeRecordingRecovered);
      unsubscribe("nativeRecordingRecoveryNeedsConfirmation", nativeRecordingRecoveryNeedsConfirmation);
      unsubscribe("gotMicrophoneNotAvailable", gotMicrophoneNotAvailable);
    };
    // eslint-disable-next-line
  }, []);

  // Detect a native recording orphaned by a webview reset: the marker survives
  // the reset; if it's still there on mount and nothing is recording, the audio
  // was never handed back (incident 2026-07-21). Native webview only — the web
  // path has its own write-ahead + drain.
  useEffect(() => {
    if (isMobileApp && auth?.user?.id && pendingNativeResumeRef.current !== null) {
      const startedAt = pendingNativeResumeRef.current;
      pendingNativeResumeRef.current = null;
      resumeNativeRecording(startedAt);
    }
    if (!isMobileApp || captureGateRef.current) return;
    const pending = readNativeRecordingMarker(auth?.user?.id, { ignoreGrace: true, ignoreAge: true });
    // A reload during conversion must reattach the receiver immediately, even
    // inside the marker grace period. Never send a second Stop to the old shell.
    if (pending?.stoppedAt) stopRecordingApp({ resumeOnly: true, marker: pending });
    const marker = readNativeRecordingMarker(auth?.user?.id);
    if (marker) {
      setLostRecording(marker);
      setLostRecordingState(marker.stoppedAt ? "notRecovered" : "idle");
    }
    // eslint-disable-next-line
  }, [isMobileApp, auth?.user?.id]);

  // Swap input stream when device changes during recording (including fallback to default)
  useEffect(() => {
    if (recording && !isMobileApp && streamRef.current) {
      replaceWebInput(currentDevice);
    }
    // eslint-disable-next-line
  }, [currentDevice]);

  const watchWebTrack = (stream) => {
    stream.getAudioTracks?.()[0]?.addEventListener(
      "ended",
      () => {
        if (!webSessionRef.current?.started || !captureGateRef.current || streamRef.current !== stream) return;
        toast.info(t("hooks.useRecorder.micDisconnectedSwitching"));
        replaceWebInput(undefined);
      },
      { once: true }
    );
  };

  const replaceWebInput = async (deviceId) => {
    if (webDeviceSwapRef.current) return;
    // The telemed room owns the microphone; the recorder only records its mix.
    if (extraDataRef.current?.telemed) return;
    const session = webSessionRef.current;
    const ctx = activeCaptureRef.current;
    if (!session?.started || !captureGateRef.current) return;
    // Inline voice actions use one container. Finish it safely instead of
    // manufacturing a second recording from an input-selection change.
    if (!ctx?.active) {
      captureGateRef.current = false;
      clearInterval(intervalRef.current);
      stopRecordingWeb();
      return;
    }
    webDeviceSwapRef.current = true;
    let expired = false;
    let timeout;
    try {
      const acquiring = navigator.mediaDevices.getUserMedia({ audio: deviceId ? { deviceId: { exact: deviceId } } : true });
      acquiring.then(
        (stream) => {
          if (expired) stopStream(stream);
        },
        () => {}
      );
      const replacement = await Promise.race([
        acquiring,
        new Promise((_, reject) => {
          timeout = setTimeout(() => {
            expired = true;
            reject(new Error("Input switch timed out"));
          }, WEB_MIC_ACQUISITION_TIMEOUT_MS);
        }),
      ]);
      if (!ctx.active || webSessionRef.current !== session || !mountedRef.current) {
        stopStream(replacement);
        return;
      }
      if (session.userId !== recordingUserRef.current?.id || session.orgId !== currentOrgId()) {
        stopStream(replacement);
        captureGateRef.current = false;
        clearInterval(intervalRef.current);
        stopRecordingWeb();
        return;
      }
      clearInterval(segmentTimerRef.current);
      const oldRecorder = mediaRecorder.current;
      if (oldRecorder?.state === "recording") {
        oldRecorder.__expectedStop = true;
        oldRecorder.stop();
      }
      stopStream(streamRef.current);
      cleanupAudioAnalysis();
      streamRef.current = replacement;
      setupAudioAnalysis(replacement);
      watchWebTrack(replacement);
      startSegment(replacement, ctx);
      segmentTimerRef.current = setInterval(() => rotateSegment(replacement, ctx), SEGMENT_MS);
    } catch (error) {
      console.error("Cannot switch recording microphone", error);
      toast.error(t("hooks.useRecorder.cannotStartRecordingDevice"));
      if (webSessionRef.current === session) {
        captureGateRef.current = false;
        clearInterval(intervalRef.current);
        stopRecordingWeb();
      }
    } finally {
      clearTimeout(timeout);
      webDeviceSwapRef.current = false;
    }
  };

  const setupAudioAnalysis = (stream) => {
    if (!stream || isMobileApp) return;

    try {
      // Create audio context for analysis
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(stream);

      // Create analyser node
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.8;

      source.connect(analyserRef.current);

      // Start monitoring volume
      startVolumeMonitoring();

      hasSpoken.current = false;
      // Set up silence detection timeout
      silenceTimeoutRef.current = setTimeout(() => {
        if (!hasSpoken.current) {
          setShowNoAudioWarning(true);
        }
      }, 3000);
    } catch (error) {
      console.error("Error setting up audio analysis:", error);
    }
  };

  const startVolumeMonitoring = () => {
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const analyseAudio = () => {
      analyserRef.current.getByteFrequencyData(dataArray);

      // Calculate average volume
      const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
      const normalizedVolume = Math.min(100, (average / 128) * 100);
      setVolumeLevel(normalizedVolume);

      // Check if there's audio activity (threshold for noise detection)
      if (normalizedVolume > 20) {
        setShowNoAudioWarning(false);
        clearTimeout(silenceTimeoutRef.current);
      }

      if (normalizedVolume > 20) {
        hasSpoken.current = true;
      }

      animationFrameRef.current = requestAnimationFrame(analyseAudio);
    };

    analyseAudio();
  };

  const getRecordingHealthCheckStorageKey = () => {
    const userId = auth?.user?.id || "unknown";
    const orgId = auth?.session?.currentOrganizationId || auth?.session?.currentOrganizationName || "default";
    return `recordingHealthCheckCompleted:${userId}:${orgId}`;
  };

  const hasCompletedRecordingHealthCheck = () => {
    const recordingHealthCheckCompleted = auth?.user?.custom?.recordingHealthCheckCompleted;
    const storageKey = getRecordingHealthCheckStorageKey();
    const storageValue = localStorage.getItem(storageKey);

    if (recordingHealthCheckCompleted === false) {
      return storageValue === "true";
    }

    return true;
  };

  const closeHealthCheckAndContinue = () => {
    setShowModalHealthCheck(false);
    localStorage.setItem(getRecordingHealthCheckStorageKey(), "true");

    if (auth?.user?.custom?.recordingHealthCheckCompleted === false) {
      updateProfile({
        custom: {
          recordingHealthCheckCompleted: true,
        },
      }).catch((error) => {
        console.log("Error saving recording health check completion:", error);
      });
    }

    const pendingAction = pendingRecordActionRef.current;
    pendingRecordActionRef.current = null;
    if (pendingAction) {
      pendingAction();
    }
  };

  // Ask native to stop-and-hand-over a recording the webview lost track of.
  // If the native recorder survived the reset, the audio arrives through the
  // normal onAudioRecorded flow (which clears the marker — that's the success
  // signal). Bounded: if the marker is still there after the timeout, concede
  // and point at Local Recordings.
  const attemptLostRecordingRescue = () => {
    if (recording || startingRecording) return; // never kill a live recording
    // One rescue attempt per surfaced marker: a retry would stack a second
    // onAudioRecorded listener inside stopRecordingApp (double-processing a
    // later audio event). A stopped marker stays guarded and points at Local
    // Recordings; hiding the dialog never reissues Stop.
    if (lostRecordingState !== "idle") return;
    setLostRecordingState("rescuing");
    stopRecordingApp(); // attaches onAudioRecorded listeners + sendStopRecording
    if (rescueTimerRef.current) clearTimeout(rescueTimerRef.current);
    rescueTimerRef.current = setTimeout(() => {
      rescueTimerRef.current = null;
      // Marker gone = the handler received audio and cleared it (success).
      if (peekNativeRecordingMarker()) {
        if (nativeHandoffRef.current) nativeHandoffRef.current.timedOut = true;
        setLostRecordingState("notRecovered");
      }
    }, LOST_RECORDING_RESCUE_TIMEOUT_MS);
  };

  const dismissLostRecording = () => {
    // Closing recovery UI is not evidence the native file finished saving.
    // Keep its marker and receiver so another start cannot replace it.
    setLostRecording(null);
  };

  const confirmLostRecordingRecovered = () => {
    const marker = peekNativeRecordingMarker();
    // A legacy retry may lack a UUID. Only offer this after a successful save,
    // and require the user to verify the original transcript before unlocking.
    if (!marker?.recoveryCandidate || marker.recordingUuid !== lostRecording?.recordingUuid) return;
    if (marker.userId !== recordingUserRef.current?.id || captureGateRef.current) return;
    releaseRecoveredNativeRecording(marker);
  };

  const confirmNativeStartFailed = () => {
    const marker = peekNativeRecordingMarker();
    if (!marker?.startFailed || marker.stoppedAt || marker.recordingUuid !== lostRecording?.recordingUuid) return;
    if (marker.userId !== recordingUserRef.current?.id || captureGateRef.current || nativeHandoffRef.current) return;
    releaseRecoveredNativeRecording(marker);
  };

  // The shell cannot tell the webview that a sync finished without this UUID,
  // so a marker whose rescue is spent and whose file is gone would block every
  // start forever. Release on the user's confirmation, only after they opened
  // Local Recordings for this marker and only when no other branch owns it.
  // Evidence that the user opened native Local Recordings for this marker.
  // The persistent stamp survives reloads; the in-memory fallback covers a
  // device whose storage still reads but refuses the stamp write (quota), so
  // the release can't be soft-locked behind a hint the user already followed.
  const sessionLocalCheckRef = useRef(new Set());
  const stampLocalRecordingsChecked = (recordingUuid) => {
    if (!recordingUuid) return;
    if (!markNativeLocalRecordingsChecked(recordingUuid)) sessionLocalCheckRef.current.add(recordingUuid);
  };

  // Shared by the modal's enable state and the release's own guard so the two
  // can never disagree — an enabled button the hook silently refuses reads as
  // broken. The stamp goes stale after LOCAL_RECORDINGS_CHECK_MAX_AGE_MS: a
  // check from days ago is not evidence the phone has no file today.
  const hasLocalRecordingsEvidence = (recordingUuid) => {
    if (!recordingUuid) return false;
    if (sessionLocalCheckRef.current.has(recordingUuid)) return true;
    const marker = peekNativeRecordingMarker();
    if (marker?.recordingUuid !== recordingUuid || !marker.localRecordingsCheckedAt) return false;
    return Date.now() - marker.localRecordingsCheckedAt <= LOCAL_RECORDINGS_CHECK_MAX_AGE_MS;
  };

  // Nothing is sent to native and nothing is deleted: a late syncRecording for
  // this UUID still saves through the normal path.
  const confirmNoRecordingToRecover = () => {
    const marker = peekNativeRecordingMarker();
    if (!marker || marker.recordingUuid !== lostRecording?.recordingUuid) return;
    if (marker.userId !== recordingUserRef.current?.id || captureGateRef.current) return;
    if (marker.recoveryCandidate || marker.startFailed) return;
    if (lostRecordingState !== "notRecovered" || !hasLocalRecordingsEvidence(marker.recordingUuid)) return;
    const handoff = nativeHandoffRef.current;
    if (handoff && !(handoff.timedOut && handoff.marker?.recordingUuid === marker.recordingUuid)) {
      // The reattached receiver may still deliver this audio. The button can
      // be enabled here (the modal can't see the handoff), so say why the
      // confirm did nothing instead of silently swallowing it.
      toast.info(t("hooks.useRecorder.previousRecordingSaving"), { toastId: "previous-recording-saving" });
      return;
    }
    reportNativeMarker("native-recording released-without-audio", marker, lostRecordingState, handoff);
    releaseRecoveredNativeRecording(marker);
  };

  useEffect(() => {
    if (!lostRecording || lostRecordingState !== "notRecovered") return;
    const marker = peekNativeRecordingMarker();
    if (!marker || marker.recordingUuid !== lostRecording.recordingUuid) return;
    if (marker.userId !== recordingUserRef.current?.id) return;
    if (marker.recoveryCandidate || marker.startFailed || marker.deadEndReportedAt) return;
    // A reattached handoff that hasn't timed out may still deliver the audio.
    // Reporting now — and burning the once-per-marker stamp — would count a
    // recovery-in-progress as a dead end; the timeout path re-surfaces the
    // marker, which re-runs this effect at the moment it truly dead-ends.
    const handoff = nativeHandoffRef.current;
    if (handoff && !(handoff.timedOut && handoff.marker?.recordingUuid === marker.recordingUuid)) return;
    // Stamp first: if storage refuses, skip the report rather than re-send it
    // on every record press.
    const reported = markNativeMarkerDeadEndReported(marker.recordingUuid);
    if (!reported) return;
    reportNativeMarker("native-recording dead-end", reported, lostRecordingState, nativeHandoffRef.current);
    // eslint-disable-next-line
  }, [lostRecording, lostRecordingState]);

  const surfaceLostNativeRecording = () => {
    if (isMobileApp && nativeHandoffRef.current) {
      const handoff = nativeHandoffRef.current;
      if (handoff.timedOut) {
        setLostRecording(handoff.marker);
        setLostRecordingState("notRecovered");
      } else {
        toast.info(t("hooks.useRecorder.previousRecordingSaving"), { toastId: "previous-recording-saving" });
      }
      return true;
    }
    if (isMobileApp && !captureGateRef.current) {
      const marker = readNativeRecordingMarker(auth?.user?.id, { ignoreGrace: true, ignoreAge: true });
      if (marker) {
        if (marker.stoppedAt && Date.now() - marker.stoppedAt < MARKER_GRACE_MS) {
          toast.info(t("hooks.useRecorder.previousRecordingSaving"), { toastId: "previous-recording-saving" });
          return true;
        }
        setLostRecording(marker);
        setLostRecordingState(marker.stoppedAt ? "notRecovered" : "idle");
        return true;
      }
    }
    return false;
  };

  const guardRecordingStart = (startFn) => {
    if (surfaceLostNativeRecording()) {
      reportBlockedStart("lost-native-recording");
      return;
    }
    if (hasCompletedRecordingHealthCheck()) {
      startFn();
      return;
    }
    pendingRecordActionRef.current = startFn;
    setShowModalHealthCheck(true);
    reportBlockedStart("health-check-pending");
  };

  // Ticket #4308: four taps on a disposition phase captured nothing, and the
  // activity trail showed only `record.start` (logged by the caller BEFORE the
  // phase modal). Every exit between the tap and the microphone stream was a
  // toast or a modal nobody could read back later. Record the refusal and its
  // reason as `record.blocked`, so support can tell a refused start from a lost
  // upload without the clinician's recollection. `reason` is an allowed
  // activity ctx key; never put a device label or an error message in it.
  const reportBlockedStart = (reason, chatId = chatIdRef.current) => {
    logActivity("record.blocked", { chatId: chatId === "quick_start" ? undefined : chatId, reason });
  };

  const cleanupAudioAnalysis = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      const context = audioContextRef.current;
      audioContextRef.current = null;
      try {
        context.close();
      } catch (e) {
        console.warn("Audio context close error:", e);
      }
    }
    setVolumeLevel(0);
    setShowNoAudioWarning(false);
  };

  // The identity stamped at start, falling back to the live session for a capture
  // that somehow armed without one (and for the native handoff, which can re-enter
  // these paths after a webview reset).
  const captureUserId = () => captureIdentityRef.current?.userId || auth?.user?.id;
  const captureLanguage = () => captureIdentityRef.current?.language || auth?.user?.custom?.transcriptLanguage || "en";

  // Warn the clinician BEFORE the encounter when this device cannot hold a normal
  // recording. Fire-and-forget: never blocks or delays the microphone, and stays
  // silent on any browser that cannot answer (see storageHeadroom).
  const warnIfStorageLow = () => {
    if (lowStorageWarnedRef.current) return;
    estimateRecordingHeadroom()
      .then((headroom) => {
        if (!isHeadroomLow(headroom) || lowStorageWarnedRef.current) return;
        lowStorageWarnedRef.current = true;
        // Reported once per session with a constant message: this is the preventable
        // half of the segment-loss cluster, so it is worth seeing in aggregate.
        console.error("Device is low on storage at recording start", {
          minutesAvailable: headroom.minutes,
          freeBytes: headroom.freeBytes,
          quota: headroom.quota,
          // embedded + persisted:false is the signature of a partitioned storage
          // bucket (the Chrome extension iframe losing its host permission), which
          // is a different problem from a genuinely full disk.
          embedded: headroom.embedded,
          persisted: headroom.persisted,
        });
        toast.warning(t("hooks.useRecorder.lowStorageWarning", { minutes: headroom.minutes }), {
          toastId: "lowStorageWarning",
        });
      })
      .catch(() => {});
  };

  // Storage can reject before a transaction opens as well as resolve false.
  // Both outcomes must retain the audio for retry under the same owner.
  const storeWebAudio = async (save, record) => {
    try {
      return await save(record);
    } catch (error) {
      console.error("Recording storage transaction failed", error);
      return false;
    }
  };

  // Re-attempt the segments whose durable write failed earlier, oldest first. Stops
  // at the first failure: they all target the same store, so if the oldest still
  // cannot be written the rest will not be either, and hammering IndexedDB every
  // rotation would only load a device that is already struggling.
  const retryPendingWrites = async (ctx) => {
    const queue = ctx.pendingWrites;
    if (!queue.length) return;
    ctx.pendingWrites = [];
    for (let i = 0; i < queue.length; i++) {
      const record = queue[i];
      const persisted = await storeWebAudio(addRecordingSegment, record);
      if (!persisted) {
        ctx.pendingWrites = queue.slice(i);
        return;
      }
      // On disk after all: it is no longer missing locally, so the assembler needs
      // no gap fill for it and the end-of-recording summary must not count it.
      ctx.lostLocally = ctx.lostLocally.filter((lost) => lost !== record.index);
      if (record.isFinal) ctx.finalPersisted = true;
      console.log("Recording segment " + record.index + " persisted on retry");
    }
  };

  const retrySegmentedSave = async (ctx) => {
    await ctx.delivery;
    await Promise.all(ctx.rescues);
    // Retry the original indices. Never reassign bytes to a later segment or
    // advertise a final count while an earlier segment exists only in memory.
    const failedChunks = ctx.failedChunks || [];
    ctx.failedChunks = [];
    for (let i = 0; i < failedChunks.length; i++) {
      const entry = failedChunks[i];
      try {
        const segmentAudio = await blobToDataUrl(new Blob(entry.chunks, { type: entry.chunks[0]?.type || "audio/webm" }));
        const persisted = await storeWebAudio(addRecordingSegment, {
          recordingId: ctx.recordingId,
          chatId: ctx.chatId,
          userId: ctx.userId,
          orgId: ctx.session.orgId,
          index: entry.index,
          isFinal: false,
          total: 0,
          language: ctx.language,
          phase: ctx.phase,
          segmentAudio,
          startedAt: ctx.startedAt,
          patientUserId: ctx.session.patientUserId,
          patientLabel: ctx.session.patientLabel,
          encounterUuid: ctx.session.encounterUuid,
          audioInfo: ctx.audioInfo || undefined,
        });
        if (!persisted) throw new Error("Recording segment retry was not committed");
        ctx.lostLocally = ctx.lostLocally.filter((index) => index !== entry.index);
      } catch (_) {
        ctx.failedChunks = failedChunks.slice(i);
        break;
      }
    }
    ctx.encodingFailed = ctx.failedChunks.length > 0;
    await retryPendingWrites(ctx);
    if (ctx.deliveryFailed || ctx.encodingFailed || ctx.lostLocally.some((index) => !ctx.rescuedToServer.has(index)))
      return false;
    if (!ctx.finalPersisted) {
      ctx.finalPersisted = await storeWebAudio(addRecordingSegment, {
        recordingId: ctx.recordingId,
        chatId: ctx.chatId,
        userId: ctx.userId,
        orgId: ctx.session.orgId,
        index: ctx.nextIndex,
        isFinal: true,
        total: ctx.nextIndex,
        language: ctx.language,
        phase: ctx.phase,
        segmentAudio: "",
        startedAt: ctx.startedAt,
        patientUserId: ctx.session.patientUserId,
        patientLabel: ctx.session.patientLabel,
        encounterUuid: ctx.session.encounterUuid,
        // The drain reads the recording's audioInfo from its final row: without it a
        // telemed recording would be transcribed as mono.
        audioInfo: ctx.audioInfo || undefined,
      });
    }
    return ctx.finalPersisted;
  };

  // Each segment is a complete file from its own MediaRecorder. It is written
  // to the durable IndexedDB queue and (additively, best-effort) streamed to the
  // server recovery store — the transcript itself is assembled later from the
  // local copies into ONE MP3, so losing a single segment costs ~SEGMENT_MS of
  // audio rather than the whole recording. The segment index is assigned here at
  // flush time (not at recorder-create time) so a totally-silent rotation window
  // that produces no chunks does not burn an index and leave a spurious gap.
  // Write-ahead durability: a finished segment is PERSISTED to the durable queue
  // first, and the resilient drain (useIndexDb.checkRecordings) is the single
  // uploader — it sends the segment, deletes it only on a server ack, and triggers
  // assembly on the final segment. Because the segment is on disk before any
  // network call, a hung upload, a closed tab, or a flaky connection can no longer
  // lose it; recovery is the drain's job (immediate attempt + backoff + on-reconnect).
  const flushSegment = async (ctx, chunks, isFinal) => {
    if (!ctx || !ctx.recordingId) return;
    const task = async () => {
      // Unconditional liveness heartbeat (block-only signal for the IndexedDB drain).
      // BEFORE the empty-segment early-return below, so a silent rotation still proves
      // the recorder is alive and the drain won't truncate it. Fire-and-forget.
      writeHeartbeat(ctx.recordingId);
      // Stamped at capture start, not read live: see captureIdentityRef.
      const language = ctx.language || captureLanguage();
      const userId = ctx.userId || captureUserId();

      let dataUrl = "";
      if (chunks && chunks.length > 0) {
        try {
          dataUrl = await blobToDataUrl(new Blob(chunks, { type: chunks[0]?.type || "audio/webm" }));
        } catch (e) {
          console.error("Failed to encode recording segment", e);
          ctx.encodingFailed = true;
          // Keep the raw bytes reachable while ownership remains held. An encode
          // failure is not an empty segment and must not produce a false final.
          const index = ctx.nextIndex++;
          ctx.lostLocally.push(index);
          (ctx.failedChunks ||= []).push({ index, chunks });
          return;
        }
      }
      const hasContent = dataUrl !== "";

      // An empty NON-final rotation contributed nothing; drop it without consuming
      // an index. An empty FINAL segment still has to go through, to carry the
      // total and trigger assembly of the segments that did arrive.
      if (!hasContent && !isFinal) return;

      // Serialize per-recording (on ctx, not a shared ref) so indices are assigned
      // in order and the final segment's total reflects every prior segment. A late
      // flush from a DIFFERENT recording has its own ctx and cannot touch this one's
      // counter.
      const idx = ctx.nextIndex;
      if (hasContent) ctx.nextIndex = idx + 1;
      const total = isFinal ? ctx.nextIndex : 0;
      // Drain any outstanding rescue BEFORE the final row lands. Persisting it wakes
      // the assembler (addRecordingSegment -> checkRecordings), and a gap the server
      // has not received yet would be assembled — and saved — as a permanent hole.
      // Only ever waits when a local write has already failed.
      if (isFinal && ctx.rescues.length) await Promise.all(ctx.rescues);
      // Earlier failures get another go on every rotation — and one last go here,
      // before the final row wakes the assembler, so a segment that can be written
      // is written rather than gap-filled.
      await retryPendingWrites(ctx);
      // A segment that carries a container header and nothing else means the track
      // delivered no audio for this whole window. Persist it anyway — the indices and
      // the final segment's total must stay honest, and the assembler already skips a
      // segment it cannot decode — but say so NOW rather than at assembly, which on
      // this path is hours later and long past anything the clinician could do.
      const encodedBytes = hasContent ? Math.round((dataUrl.length - (dataUrl.indexOf(",") + 1)) * 0.75) : 0;
      if (hasContent && encodedBytes < MIN_SEGMENT_BYTES) {
        ctx.silentSegments = (ctx.silentSegments || 0) + 1;
        setShowNoAudioWarning(true);
        if (!ctx.silentReported) {
          ctx.silentReported = true;
          console.error("Recording segment carries no audio; the microphone is delivering nothing", {
            recordingId: ctx.recordingId,
            index: idx,
            bytes: encodedBytes,
          });
          toast.error(t("hooks.useRecorder.microphoneWentSilent"), { toastId: "microphoneWentSilent" });
        }
      }

      const finalReady = !ctx.encodingFailed && !ctx.lostLocally.some((index) => !ctx.rescuedToServer.has(index));
      const record = {
        recordingId: ctx.recordingId,
        chatId: ctx.chatId,
        userId,
        orgId: ctx.session.orgId,
        index: idx,
        // A failed earlier encode left bytes outside durable custody. Never
        // let assembly or a reload mistake this set for a complete recording.
        isFinal: isFinal && finalReady,
        total: finalReady ? total : 0,
        language,
        phase: ctx.phase,
        segmentAudio: dataUrl,
        startedAt: ctx.startedAt,
        patientUserId: ctx.session.patientUserId,
        patientLabel: ctx.session.patientLabel,
        encounterUuid: ctx.session.encounterUuid,
        audioInfo: ctx.audioInfo || undefined,
      };
      const persisted = await storeWebAudio(addRecordingSegment, record);

      // Additive durability backstop (A2): stream this segment to the server. This
      // does NOT drive transcription — the client assembles ONE MP3 from the
      // IndexedDB copy via the durable drain, which stays the transcript source
      // whenever the local copy is intact. Double-guarded (try/catch + a mapped
      // rejection) exactly like api.recordingBegin above, so it can never throw into
      // or block the recording flow; it 404s harmlessly until the backend "Recording
      // - Store Segment" process is deployed. Fired regardless of local persist
      // outcome, and the promise is KEPT rather than discarded: if the IndexedDB
      // write turns out to have failed, this call is the only copy of the audio and
      // the branch below promotes it from best-effort to retried-until-it-lands.
      let serverCopy = null;
      const canUpload = () => ctx.session.userId === recordingUserRef.current?.id && ctx.session.orgId === currentOrgId();
      if (hasContent && canUpload()) {
        try {
          serverCopy = api
            .recordingSegment(ctx.recordingId, ctx.chatId, idx, dataUrl, record.isFinal, record.total, language)
            .then(
              () => true,
              () => false
            );
        } catch (_) {
          serverCopy = Promise.resolve(false);
        }
      }

      if (persisted) {
        if (record.isFinal) ctx.finalPersisted = true;
        return;
      }
      // Durable IndexedDB store failed (quota, an aborted transaction, a session that
      // lapsed mid-recording). These ~10s of clinical audio now exist ONLY in the
      // server backstop and in the closure below, so the rescue is retried rather
      // than hoped for, and the assembler pulls the segment back from the server at
      // stitch time (useIndexDb assembleAndUploadSegments) so the transcript is
      // complete rather than silently short.
      //
      // ONE report per recording, with a constant message: the recorder rotates every
      // SEGMENT_MS, so a full disk used to emit an event and a toast every 10s — and
      // the index in the message text split them into a fresh issue each time.
      if (!ctx.storageFailureReported) {
        ctx.storageFailureReported = true;
        console.error("Recording segment could not be persisted locally; rescuing to the server backstop", {
          recordingId: ctx.recordingId,
          index: idx,
        });
      }
      ctx.lostLocally.push(idx);
      // Keep the audio in memory for another attempt on the next rotation. The
      // Only a confirmed server copy permits eviction of an older retry entry.
      if (hasContent || isFinal) {
        if (ctx.pendingWrites.length >= MAX_PENDING_WRITES) {
          const safeToDrop = ctx.pendingWrites.findIndex((pending) => ctx.rescuedToServer.has(pending.index));
          if (safeToDrop >= 0) ctx.pendingWrites.splice(safeToDrop, 1);
        }
        ctx.pendingWrites.push(record);
      }
      // Deliberately NOT awaited on ctx.chain — the next segment's flush must never
      // queue behind a retry — but tracked on ctx so the final flush can wait for it.
      ctx.rescues.push(
        rescueSegmentToServer({
          recordingId: ctx.recordingId,
          chatId: ctx.chatId,
          index: idx,
          dataUrl,
          isFinal: record.isFinal,
          total: record.total,
          language,
          firstAttempt: serverCopy,
          canUpload,
        })
          .then((rescued) => {
            if (rescued) ctx.rescuedToServer.add(idx);
            if (record.isFinal && rescued) ctx.finalServerSaved = true;
            // Alarm the clinician only when the audio is genuinely at risk: a rescued
            // segment reaches the transcript through the assembler's gap fill, so a
            // toast there would be a false alarm mid-encounter. Once per recording.
            if (!rescued && !ctx.storageFailureToasted) {
              ctx.storageFailureToasted = true;
              toast.error(t("hooks.useRecorder.segmentStorageFailed"), { toastId: "segmentStorageFailed" });
            }
            return rescued;
          })
          // A toast failure must never reject the chain the final flush awaits.
          .catch(() => false)
      );
      return;
    };
    ctx.chain = ctx.chain.then(task, task);
    await ctx.chain;
    // On the FINAL flush this recording is done — release the cross-tab liveness
    // signals so the drain may rebuild a sibling stalled set. The unconditional
    // top-of-flush write above re-stamped the heartbeat for this final segment, so
    // clearing it HERE (not in finalizeSegmentedCapture) is the last word.
    if (isFinal) {
      clearHeartbeat(ctx.recordingId);
      clearActiveRecording(ctx.recordingId);
      if (ctx.lostLocally.length) {
        // One summary per recording, once every rescue has settled. `lost` counts
        // the segments still missing from this device after the retries — a segment
        // written on a later attempt is not in it — and `unrecoverable` is the only
        // count that means audio nobody holds.
        await Promise.all(ctx.rescues);
        const stillMissing = ctx.lostLocally;
        const unrecoverable = stillMissing.filter((index) => !ctx.rescuedToServer.has(index));
        console.error("Recording finished with segments missing from local storage", {
          recordingId: ctx.recordingId,
          lost: stillMissing.length,
          rescuedToServer: stillMissing.length - unrecoverable.length,
          unrecoverable: unrecoverable.length,
        });
      }
      const hasUnsaved = ctx.lostLocally.some((index) => !ctx.rescuedToServer.has(index));
      await releaseWebSession(ctx.session, !ctx.encodingFailed && !hasUnsaved && (ctx.finalPersisted || ctx.finalServerSaved));
    }
  };

  const startSegment = (stream, ctx) => {
    const chunks = [];
    let finish;
    let finished = false;
    const stopped = new Promise((resolve) => {
      finish = resolve;
    });
    let rec;
    try {
      rec = new MediaRecorder(stream, { audioBitsPerSecond: 48000 });
    } catch (_) {
      rec = new MediaRecorder(stream);
    }
    rec.__isFinal = false;
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    // Close over THIS segment's context so a flush always lands on the recording
    // it belongs to, even if a newer recording has since started.
    rec.onstop = () => {
      if (finished) return;
      finished = true;
      if (!rec.__expectedStop && ctx.active && !webDeviceSwapRef.current) {
        captureGateRef.current = false;
        clearInterval(intervalRef.current);
        stopRecordingWeb();
      }
      finish();
    };
    rec.onerror = (e) => {
      console.error("Segment recorder error, salvaging captured audio", e?.error || e);
      // MediaRecorder emits its final dataavailable and stop after error.
      // Consuming here as well would save the same segment twice.
      if (ctx.active && mediaRecorder.current === rec) {
        captureGateRef.current = false;
        clearInterval(intervalRef.current);
        stopRecordingWeb();
      }
    };
    mediaRecorder.current = rec;
    rec.start();
    // Reserve order when the segment starts, before asynchronous stop/encoding
    // callbacks can complete out of order. Final assembly cannot overtake audio.
    ctx.delivery = ctx.delivery
      .then(() => stopped)
      .then(() => flushSegment(ctx, chunks, rec.__isFinal === true))
      .catch((error) => {
        ctx.deliveryFailed = true;
        ctx.encodingFailed = true;
        console.error("Recording segment could not be saved", error);
        if (rec.__isFinal) releaseWebSession(ctx.session, false);
      });
  };

  const rotateSegment = (stream, ctx) => {
    if (!ctx || !ctx.active) return;
    const rec = mediaRecorder.current;
    if (!rec || rec.state !== "recording") return;
    try {
      rec.__isFinal = false;
      rec.__expectedStop = true;
      rec.stop(); // → onstop → flushSegment(ctx, thisChunks, false)
      startSegment(stream, ctx); // immediately begin the next segment
    } catch (error) {
      console.error("Recording segment rotation failed; finalizing captured audio", error);
      captureGateRef.current = false;
      clearInterval(intervalRef.current);
      stopRecordingWeb();
      toast.error(t("hooks.useRecorder.cannotStartRecordingDevice"));
    }
  };

  // The document is going away with a capture still running — the Chrome extension's
  // side panel being closed mid-recording is the case this was written for, where the
  // microphone is released and the rotation timer dies with no chance to finalize.
  //
  // Two things happen, in this order, because only the first is guaranteed to finish.
  // markCaptureInterrupted is a synchronous localStorage write, so it lands even when
  // the document is torn down immediately; it tells the next session that this
  // finalless set is dead rather than young, which is the difference between the
  // drain rebuilding it at once and waiting out a 120s inference. Stop is then
  // attempted for the case where the document survives long enough to persist
  // the final audio. Ordinary visibility changes do not stop capture.
  useEffect(() => {
    const onPageHide = () => {
      const session = webSessionRef.current;
      if (!session) return;
      session.ownership.update("interrupted");
      markCaptureInterrupted(session.recordingUuid);
      captureGateRef.current = false;
      webStartTokenRef.current += 1;
      clearInterval(intervalRef.current);
      try {
        stopRecordingWeb();
      } catch (_) {
        // Best-effort by definition: the document may not survive long enough.
      }
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const beginSegmentedCapture = (stream, restoreScroll) => {
    const session = webSessionRef.current;
    const recordingId = session.recordingUuid;
    // Fresh, isolated context for this recording. Nothing here is shared with any
    // other recording, so overlapping pause→record cycles cannot cross-contaminate.
    // startedAt lives on the context (not a shared ref) so each recording's
    // transcript block is stamped with its OWN real start time on assembly.
    const startedAt = Date.now();
    const ctx = {
      session,
      recordingId,
      chatId: chatIdRef.current,
      phase: phaseRef.current || "",
      startedAt,
      // Telemed: {channels: 2, channelLabels, audioSource}, stored with every segment so the
      // assembled recording is transcribed per channel. null for a microphone recording.
      audioInfo: extraDataRef.current?.telemed?.audioInfo || null,
      // This capture's owner and language, fixed for its whole life so a session
      // that lapses mid-recording cannot orphan the segments that follow.
      userId: captureUserId(),
      language: captureLanguage(),
      nextIndex: 0,
      active: true,
      chain: Promise.resolve(),
      delivery: Promise.resolve(),
      // Segments whose local write failed, and the in-flight server rescues for
      // them. Per-recording (not a shared ref) so one recording's storage trouble
      // can never be attributed to another.
      lostLocally: [],
      rescues: [],
      rescuedToServer: new Set(),
      // Segments that carried no audio, and whether the clinician has been told.
      silentSegments: 0,
      silentReported: false,
      // Failed writes still held in memory, retried on the next rotation.
      pendingWrites: [],
      storageFailureReported: false,
      storageFailureToasted: false,
    };
    activeCaptureRef.current = ctx;
    session.segmentContext = ctx;
    session.retrySave = () => retrySegmentedSave(ctx);

    // Mark this tab's live capture + seed the heartbeat so a concurrent drain pass
    // in this same browser never treats a just-started recording as a stalled set
    // to rebuild (the heartbeat BLOCKS; segment-recency authorizes — spec §14A).
    setActiveRecording(recordingId);
    writeHeartbeat(recordingId);

    // If the segmented recorder can't even start (e.g. MediaRecorder.start throws),
    // tear down the half-armed capture and report failure so the caller falls back
    // to the proven single-blob path. No audio has been captured yet, so the
    // fallback loses nothing.
    try {
      startSegment(stream, ctx);
      session.started = true;
    } catch (err) {
      console.error("Segmented capture failed to start; falling back to single-blob", err);
      if (segmentTimerRef.current) {
        clearInterval(segmentTimerRef.current);
        segmentTimerRef.current = null;
      }
      ctx.active = false;
      activeCaptureRef.current = null;
      mediaRecorder.current = null;
      return false;
    }

    // Capture is live — open the server-side manifest ONLY now. Opening it after a
    // confirmed start means a fallback to the single-blob path (above) never
    // orphans a manifest for a recordingId that will receive no segments. The first
    // segment doesn't upload for ~SEGMENT_MS, so this is still well ahead of any
    // segment reaching the backend.
    // Fire-and-forget: a failure to open the manifest must never block recording.
    // Two guards because there are two ways this can fail: try/catch for a
    // synchronous throw (e.g. localStorage access), and .catch for the async
    // request rejection (a 422 that a plain try/catch can't see, since the promise
    // isn't awaited). Either alone leaves a gap.
    try {
      api.recordingBegin(recordingId, ctx.chatId, getDeviceId(), getDeviceLabel(), ctx.phase, startedAt, "web").catch(() => {});
    } catch (_) {}

    // Clear any prior rotation timer before arming a new one. If a previous
    // capture wasn't finalized first, its interval would otherwise leak and keep
    // calling rotateSegment with the OLD ctx against the NEW mediaRecorder.current,
    // mis-tagging this recording's segments with the previous recording's id.
    if (segmentTimerRef.current) clearInterval(segmentTimerRef.current);
    segmentTimerRef.current = setInterval(() => rotateSegment(stream, ctx), SEGMENT_MS);
    setRecording(true);
    restoreScroll && restoreScroll();
    return true;
  };

  const finalizeSegmentedCapture = () => {
    const ctx = activeCaptureRef.current;
    if (!ctx || !ctx.active) return;
    // The heartbeat + active marker are cleared INSIDE the final flush (flushSegment's
    // isFinal branch re-stamps then clears them), not here — so a finalize that races
    // a pending final flush doesn't clear a marker the flush is about to re-write. A
    // stale heartbeat self-heals (it ages out of the freshness window) and the active
    // marker is overwritten by the next beginSegmentedCapture.
    ctx.active = false;
    if (segmentTimerRef.current) {
      clearInterval(segmentTimerRef.current);
      segmentTimerRef.current = null;
    }
    const rec = mediaRecorder.current;
    if (rec && rec.state === "recording") {
      rec.__isFinal = true; // onstop → flushSegment(ctx, ..., true) → persist + assemble
      rec.__expectedStop = true;
      rec.stop();
    } else {
      // An ended/erroring input can already be inactive. Its pending stop still
      // owns bytes; append the final marker only after that delivery finishes.
      ctx.delivery = ctx.delivery.then(() => flushSegment(ctx, [], true));
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    // This recording is no longer the active capture. Its pending final flush
    // still holds ctx via closure, so releasing the ref here is safe.
    activeCaptureRef.current = null;
  };

  const startRecording = (chatId, time, phase, extraData, restoreScroll, forceRuralMode, callback) => {
    // Chokepoint guards. Every start path funnels through here, so enforce
    // both invariants ONCE instead of at each call site (the Input-tab record
    // link, reassessment, procedure notes, disposition restarts historically
    // skipped every check).
    //
    // 1) Re-entrancy: never accept a start while a capture is in flight
    //    (double-tap, tap during the getUserMedia permission prompt, or an
    //    entry point still rendered mid-recording).
    if (captureGateRef.current) {
      console.log("Ignoring start: a capture is already in flight", chatId);
      reportBlockedStart("capture-in-flight", chatId);
      return;
    }
    if (!isMobileApp) {
      if (webSessionRef.current?.released) webSessionRef.current = null;
      const detached = navigator.locks && detachedWebSessions.get(navigator.locks);
      if (!webSessionRef.current && detached && !detached.released) webSessionRef.current = detached;
    }
    if (!isMobileApp && webSessionRef.current) {
      const session = webSessionRef.current;
      if (session.userId !== recordingUserRef.current?.id || session.orgId !== currentOrgId()) {
        toast.info(t("hooks.useRecorder.webRecordingOtherAccount"), { toastId: "web-recording-blocked" });
        reportBlockedStart("session-other-account", chatId);
        return;
      }
      if (session.saveFailed) {
        setWebRecordingRecovery({
          journal: session.ownership.journal,
          custody: { status: "unsaved" },
          live: true,
          retrying: !!session.retrying,
        });
        reportBlockedStart("session-save-failed", chatId);
        return;
      }
      toast.info(t("hooks.useRecorder.previousRecordingSaving"), { toastId: "previous-recording-saving" });
      reportBlockedStart("session-still-saving", chatId);
      return;
    }
    // Some entry points call startRecording directly. Enforce the durable
    // native guard here too, before replacing the marker or sending Begin.
    if (surfaceLostNativeRecording()) {
      reportBlockedStart("lost-native-recording", chatId);
      return;
    }
    // 2) Transcribe lock: a non-interactive start while this encounter's
    //    transcript is still in flight would capture a stale duration baseline
    //    and override the landing transcript. Key on chat.transcribing ONLY —
    //    it is armed synchronously at stop and released by EVERY path
    //    (completion SSE, watchdog, failure-clears, backstop). The recorder's
    //    backstop timer deliberately outlives the happy-path release, so it
    //    must not be read as lock truth. Interactive (callback) flows return
    //    text to a field, never the transcript, and stay unblocked — matching
    //    docs/superpowers/specs/2026-06-30-lock-record-while-transcribing-design.md.
    if (!callback && chatId && chatsRef.current?.find((c) => c.id === chatId)?.transcribing) {
      console.log("Ignoring start: encounter is still transcribing", chatId);
      reportBlockedStart("transcribing", chatId);
      return;
    }
    captureGateRef.current = true;
    // Stamp the capturing identity at the chokepoint every start funnels through,
    // so every write this recording makes is attributed to whoever armed it.
    captureIdentityRef.current = {
      userId: auth?.user?.id,
      language: auth?.user?.custom?.transcriptLanguage || "en",
    };
    warnIfStorageLow();
    const webStartToken = ++webStartTokenRef.current;
    console.log("Starting recording");
    setStartingRecording(true);
    setQuickstart(chatId === "quick_start");
    recordStartedAtRef.current = Date.now();
    setRecordingElapsed(0);

    chatIdRef.current = chatId;
    phaseRef.current = phase;
    extraDataRef.current = extraData;
    callbackRef.current = callback;
    setRecordingVariable(extraData?.variable);
    const initialTime = time || 0;
    setTime(initialTime);

    startTimeRef.current = Date.now() - initialTime * 1000;

    // A previous start that failed after arming its ticker (e.g. permission
    // denied) leaves an interval behind; never stack a second one.
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      setRecordingElapsed(Math.floor((Date.now() - recordStartedAtRef.current) / 1000));
    }, 1000);

    if (isMobileApp) {
      restoreScroll && restoreScroll();
      startRecordingApp(forceRuralMode);
    } else {
      acquireWebSession(restoreScroll, webStartToken);
    }
  };

  const startRecordingApp = (forceRuralMode) => {
    let ruralMode = RURAL_MODE_ENABLED && (auth.user.custom.ruralMode || forceRuralMode);
    if (phaseRef.current === "AI Update Note") ruralMode = false;
    setRuralMode(ruralMode);

    // Mint a stable UUID for this recording and open the server-side manifest so
    // the recording is known to the backend from the start. The native side keys
    // its internal handshake on the SQLite rowid; this UUID is what reaches the
    // backend (dedup + cross-device manifest), so a recording stuck on this phone
    // becomes visible to the user's other devices.
    const recordingUuid = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : uuidv4();
    nativeStartRef.current = { recordingUuid, userId: auth?.user?.id };
    // Write-ahead marker: survives a webview reset so an orphaned recording is
    // detectable on the next load (see ModalLostRecording). Cleared when native
    // hands the audio back (onAudioRecorded) or custody passes to native's own
    // queue (offline / error paths).
    const markerIdentity = capturePatient(chatIdRef.current);
    writeNativeRecordingMarker({
      recordingUuid,
      chatId: chatIdRef.current,
      phase: phaseRef.current || "",
      startedAt: Date.now(),
      userId: auth?.user?.id,
      // The patient and encounter UUID this recording is for, so the handoff can be
      // judged against the encounter id's current owner (ids are recycled after hard
      // deletes).
      patientUserId: markerIdentity.patientUserId,
      encounterUuid: markerIdentity.encounterUuid,
    });
    // Only open a manifest for recordings tied to a real encounter. Quick Start /
    // AI Update Note have no real chatId yet, so the platform `chat` scope would be
    // the "quick_start" placeholder and 422. Fire-and-forget with two guards:
    // try/catch for a synchronous throw, .catch for the async rejection (the 422 a
    // plain try/catch can't see, since the promise isn't awaited).
    if (!isNonIdempotentRecording(chatIdRef.current, phaseRef.current)) {
      try {
        api
          .recordingBegin(
            recordingUuid,
            chatIdRef.current,
            getDeviceId(),
            getDeviceLabel(),
            phaseRef.current || "",
            Date.now(),
            "native"
          )
          .catch(() => {});
      } catch (_) {}
    }

    sendStartRecording(chatIdRef.current, auth.user.id, phaseRef.current, extraDataRef.current, ruralMode, recordingUuid);
    setRecording(true);
  };

  const startRecordingWeb = (restoreScroll, webStartToken) => {
    const session = webSessionRef.current;
    if (!session) return;
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      // Request mono at 16 kHz — matches mobile app capture settings and reduces
      // the raw blob size before encoding. Browsers fall back gracefully when a
      // constraint can't be satisfied.
      const audioConstraints = {
        channelCount: 1,
        sampleRate: 16000,
        echoCancellation: true,
        noiseSuppression: true,
      };
      if (currentDevice) {
        audioConstraints.deviceId = { exact: currentDevice };
      }
      const audio = audioConstraints;
      // Release the "starting" state at the moment of timeout. Deliberately does
      // NOT bump webStartTokenRef: bumping it here invalidated this attempt's own
      // token, so the .catch below early-returned and the timeout failed with no
      // toast and no permission modal at all. A late getUserMedia resolution is
      // already discarded by the `settled` latch in acquireStream.
      const releasePendingStart = () => {
        if (webStartToken !== webStartTokenRef.current) return false;
        captureGateRef.current = false;
        setStartingRecording(false);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        if (!session.started) releaseWebSession(session, true);
        return true;
      };
      const acquireStream = (constraints) =>
        new Promise((resolve, reject) => {
          let settled = false;
          const timeout = setTimeout(() => {
            if (settled) return;
            settled = true;
            releasePendingStart();
            reject(Object.assign(new Error("Microphone request timed out"), { name: "TimeoutError" }));
          }, WEB_MIC_ACQUISITION_TIMEOUT_MS);
          navigator.mediaDevices.getUserMedia(constraints).then(
            (stream) => {
              clearTimeout(timeout);
              if (settled || webStartToken !== webStartTokenRef.current) {
                stopStream(stream);
                return;
              }
              settled = true;
              resolve(stream);
            },
            (error) => {
              clearTimeout(timeout);
              if (settled) return;
              settled = true;
              reject(error);
            }
          );
        });

      const abandonStream = (stream) => {
        stopStream(stream);
        if (streamRef.current === stream) {
          streamRef.current = null;
          cleanupAudioAnalysis();
        }
        if (webStartToken === webStartTokenRef.current) {
          captureGateRef.current = false;
          setStartingRecording(false);
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          if (!session.started) releaseWebSession(session, true);
        }
      };

      const handleStream = async (stream) => {
        const isCurrent = () =>
          mountedRef.current &&
          webStartToken === webStartTokenRef.current &&
          session.userId === recordingUserRef.current?.id &&
          session.orgId === currentOrgId();
        if (!isCurrent()) return abandonStream(stream);
        try {
          if (!session.ownership.update("recording")) {
            abandonStream(stream);
            toast.error(t("hooks.useRecorder.webRecordingUnavailable"));
            reportBlockedStart("journal-write-failed", session.chatId);
            return;
          }
          await getAudioDevices();
        } catch (error) {
          console.error("Cannot enumerate recording devices:", error);
          abandonStream(stream);
          toast.error(t("hooks.useRecorder.cannotStartRecording"));
          reportBlockedStart("mic-enumerate-failed", session.chatId);
          return;
        }
        if (!isCurrent()) return abandonStream(stream);
        streamRef.current = stream;

        try {
          // Setup audio analysis for volume monitoring and silence detection
          setupAudioAnalysis(stream);

          // If the active input track ends (e.g., device unplug/disconnect), restart using default
          const audioTracks = stream.getAudioTracks();
          if (audioTracks && audioTracks[0]) {
            const inputTrack = audioTracks[0];
            // A telemed mix is not a device: never swap it for a microphone.
            if (!extraDataRef.current?.telemed) watchWebTrack(stream);

            // A MUTED track is not an ended one, and nothing was listening for it.
            // The OS hands the microphone to another app, a headset switches profile,
            // the device is reassigned — the track stays live, delivers no frames, and
            // MediaRecorder keeps writing header-only files for the rest of the
            // encounter. The recording looks fine until assembly finds no audio in it
            // (LUCASHEALTH-S5/RQ). Say so while the clinician can still do something.
            inputTrack.addEventListener("mute", () => {
              if (!activeCaptureRef.current && !recording) return;
              setShowNoAudioWarning(true);
              const ctx = activeCaptureRef.current;
              if (ctx && ctx.silentReported) return;
              if (ctx) ctx.silentReported = true;
              console.error("Microphone stopped delivering audio mid-recording (track muted)", {
                recordingId: ctx?.recordingId,
                deviceLabel: inputTrack.label || undefined,
              });
              toast.error(t("hooks.useRecorder.microphoneWentSilent"), { toastId: "microphoneWentSilent" });
            });
            inputTrack.addEventListener("unmute", () => {
              setShowNoAudioWarning(false);
              console.log("Microphone is delivering audio again (track unmuted)");
            });
          }

          // Streamed-segment path: each ~10s segment is uploaded + transcribed as it
          // completes, so a lost segment costs ~10s instead of the whole recording.
          // Flag-gated; otherwise fall through to the unchanged single-blob capture.
          // Take it ONLY for recordings the segmented path can actually serve — the
          // same set the single-blob path write-aheads (see `!isInteractive &&
          // !nonIdempotent` below):
          //   - NOT non-idempotent (Quick Start / AI Update Note): Quick Start has no
          //     real encounter chatId, so the platform `chat` scope would be the
          //     "quick_start" placeholder, rejected 422 "The chat (quick_start) is not
          //     valid" (see recordingIdempotency.js for the same set).
          //   - NOT interactive (has a callback — inline transcribe, voice section
          //     edit, Chiro onTranscript): the segmented finalize path never invokes
          //     callbackRef.current, so the transcript would silently never reach the
          //     caller. Those must stay on the inline path that passes the callback.
          // Telemed always takes the segmented path: it is the one that keeps both channels.
          if (
            (segmentedRecordingEnabled() || !!extraDataRef.current?.telemed) &&
            !isNonIdempotentRecording(chatIdRef.current, phaseRef.current) &&
            !callbackRef.current
          ) {
            // If segmented capture can't initialize, fall through to the single-blob
            // path below (no audio captured yet → nothing lost).
            if (beginSegmentedCapture(stream, restoreScroll)) return;
          }

          // Request a low capture bitrate matching the mobile app's 48 kbps target.
          // Wrap in try/catch because some browsers may reject unrecognised options.
          try {
            mediaRecorder.current = new MediaRecorder(stream, { audioBitsPerSecond: 48000 });
          } catch (_) {
            mediaRecorder.current = new MediaRecorder(stream);
          }

          // Collect chunks and parse ONCE on stop. MediaRecorder may deliver a
          // long recording as MULTIPLE dataavailable events; parsing per-event
          // ran the full AI pipeline per chunk — two concurrent rewrites of the
          // same section racing each other, with the tail chunk's rewrite (which
          // never heard the dictation) able to land last and clobber the real
          // one on screen. One recording must yield exactly one parse of the
          // complete audio.
          const recordedChunks = [];
          let recordingFlushed = false;
          mediaRecorder.current.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) {
              recordedChunks.push(e.data);
            }
          };
          const flushRecording = async () => {
            if (recordingFlushed) return;
            recordingFlushed = true;
            if (!recordedChunks.length) {
              clearTranscribeLock(session.chatId);
              await releaseWebSession(session, true);
              return;
            }
            const blob = new Blob(recordedChunks, { type: recordedChunks[0]?.type || "audio/webm" });
            session.blob = blob;
            const recordingId = session.recordingUuid;
            const nonIdempotent = isNonIdempotentRecording(session.chatId, session.phase);
            const record = {
              chatId: session.chatId,
              userId: session.userId,
              orgId: session.orgId,
              blob,
              phase: session.phase,
              extraData: session.extraData,
              recordingId,
              durationSecs: Math.max(0, Math.round((Date.now() - session.startedAt) / 1000)),
              startedAt: session.startedAt,
              patientUserId: session.patientUserId,
              patientLabel: session.patientLabel,
              encounterUuid: session.encounterUuid,
            };
            const parse = async () => {
              if (session.userId !== recordingUserRef.current?.id || session.orgId !== currentOrgId()) {
                throw new Error("Recording account changed before upload");
              }
              const outcome = await parseBlob(
                blob,
                session.chatId,
                session.phase,
                session.extraData,
                session.callback,
                recordingId
              );
              if (outcome === RECORDING_SAVE_DEFERRED_OFFLINE) return false;
              recordWebRecordingSaved({ recordingId, userId: session.userId, orgId: session.orgId });
              return true;
            };
            session.retrySave = async () => {
              let stored = false;
              try {
                // Freeze this recorder's identity before any delayed stop/encoding
                // callback. Hold ownership through the actual storage commit.
                if (!session.callback && !nonIdempotent) {
                  stored = await storeWebAudio(addRecording, record);
                  if (!stored) stored = await parse();
                } else {
                  stored = await parse();
                }
              } catch (error) {
                if (session.callback && !nonIdempotent) stored = await storeWebAudio(addRecording, record);
                if (!stored) {
                  clearTranscribeLock(session.chatId);
                  toast.error(
                    t(
                      error?.serverUnavailable
                        ? "hooks.useRecorder.recordingNotQueuedOffline"
                        : "hooks.useRecorder.errorSavingRecordingWeb"
                    )
                  );
                  console.error("Recording has not reached durable storage", error);
                }
              }
              return stored;
            };
            // Unsaved bytes stay reachable on the session and block replacement.
            // A successful queue commit does not wait for network transcription.
            const stored = await session.retrySave();
            await releaseWebSession(session, stored);
          };

          mediaRecorder.current.onstop = () => {
            if (!session.finishing) {
              captureGateRef.current = false;
              clearInterval(intervalRef.current);
              stopRecordingWeb();
            }
            return flushRecording();
          };
          // Salvage whatever audio was captured if the recorder errors out
          // mid-recording (device yanked, encoder failure) — without this the
          // accumulated chunks are silently dropped and the dictation is lost.
          // The recordingFlushed latch makes a subsequent onstop a no-op.
          mediaRecorder.current.onerror = (e) => {
            console.error("MediaRecorder error, salvaging captured audio", e?.error || e);
            // The final dataavailable precedes stop, but follows error. Let
            // onstop consume it once; an early flush would discard the tail.
            captureGateRef.current = false;
            clearInterval(intervalRef.current);
            stopRecordingWeb();
          };

          if (webStartToken !== webStartTokenRef.current) return abandonStream(stream);
          mediaRecorder.current.start();
          session.started = true;
          setRecording(true);
          restoreScroll && restoreScroll();
        } catch (error) {
          console.error("Cannot initialize recording from web:", error);
          abandonStream(stream);
          toast.error(t("hooks.useRecorder.cannotStartRecording"));
          reportBlockedStart("recorder-init-failed", session.chatId);
        }
      };

      // Telemed: record the room's two-channel mix (clinician | patient) instead of opening a
      // second microphone capture, whose echo cancellation would remove the patient's voice.
      const telemedStream = extraDataRef.current?.telemed?.stream;
      if (telemedStream) {
        handleStream(telemedStream);
        return;
      }

      acquireStream({ audio })
        .then(handleStream)
        .catch(async function (err) {
          if (webStartToken !== webStartTokenRef.current) return;
          if (err.name === "OverconstrainedError" && currentDevice) {
            setCurrentDevice(undefined);
            try {
              const fallbackStream = await acquireStream({ audio: true });
              if (webStartToken !== webStartTokenRef.current) return abandonStream(fallbackStream);
              await handleStream(fallbackStream);
              return;
            } catch (fallbackError) {
              console.error(fallbackError);
              toast.error(t("hooks.useRecorder.cannotStartRecordingDevice"));
              reportBlockedStart("mic-device-fallback-failed", session.chatId);
            }
          } else if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
            setShowModalRecordingPermission(true);
            reportBlockedStart("mic-denied", session.chatId);
          } else if (err.name === "TimeoutError") {
            // The OS/browser permission prompt outlived the acquisition window
            // (a user reading the dialog can easily take this long). Say so —
            // silence here reads as "the Record button is broken".
            console.error("Microphone acquisition timed out before the permission prompt was answered");
            toast.error(t("hooks.useRecorder.micRequestTimedOut"));
            reportBlockedStart("mic-timeout", session.chatId);
          } else {
            console.error("Cannot start recording from web:", err.toString(), err);
            toast.error(t("hooks.useRecorder.cannotStartRecording"));
            reportBlockedStart("mic-error", session.chatId);
          }
          // Terminal start failure (denied / no device / unsupported error): clear
          // the "starting" flag so UI gated on it (recording lockout, Chiro
          // "Starting…") doesn't stay stuck, and release the re-entrancy gate
          // so a retry is accepted. The OverconstrainedError fallback returns
          // early on success, so reaching here always means failure.
          captureGateRef.current = false;
          setStartingRecording(false);
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          if (!session.started) releaseWebSession(session, true);
        });
    } else {
      toast.error(t("hooks.useRecorder.notSupportedOnBrowser"));
      console.error("getUserMedia not supported on your browser!");
      captureGateRef.current = false;
      setStartingRecording(false);
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      if (!session.started) releaseWebSession(session, true);
    }
  };

  async function getAudioDevices() {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = devices.filter((device) => device.kind === "audioinput");
    setDevices(audioInputs);
    return audioInputs;
  }

  // Lock the record button for one encounter while its recording is in flight.
  // Sets the same per-chat `transcribing` flag the transcription pipeline uses,
  // so the existing release machinery (completion SSE, watchdog, parseRecording's
  // catch) clears it on the happy and error paths. The backstop covers anything
  // those miss (e.g. a dead IndexedDB drain after a successful write-ahead).
  const armTranscribeLock = (chatId) => {
    if (!chatId) return;
    updateChatValue(chatId, { transcribing: true });
    if (lockTimersRef.current[chatId]) clearTimeout(lockTimersRef.current[chatId]);
    lockTimersRef.current[chatId] = setTimeout(() => {
      // Use the freshest updateChatValue (see updateChatValueRef above) so this
      // deferred clear merges onto the CURRENT chat, never a stale snapshot.
      updateChatValueRef.current(chatId, { transcribing: false });
      delete lockTimersRef.current[chatId];
    }, TRANSCRIBE_LOCK_BACKSTOP_MS);
  };

  const clearTranscribeLock = (chatId) => {
    if (!chatId) return;
    if (lockTimersRef.current[chatId]) {
      clearTimeout(lockTimersRef.current[chatId]);
      delete lockTimersRef.current[chatId];
    }
    updateChatValueRef.current(chatId, { transcribing: false });
  };

  const stopRecording = () => {
    // Min-duration gate: ignore a stop within the first 3s of an active
    // recording (accidental double-tap). Central so EVERY caller is covered,
    // including generate-while-recording. UI mirrors this via canStopRecording.
    if (recording && recordStartedAtRef.current && Date.now() - recordStartedAtRef.current < MIN_RECORDING_MS) {
      return;
    }
    // The capture is ending: release the re-entrancy gate so the next start is
    // accepted. The transcribe lock armed below takes over gating re-record.
    webStartTokenRef.current += 1;
    captureGateRef.current = false;
    clearInterval(intervalRef.current);
    cleanupAudioAnalysis();

    // Lock the record button for THIS encounter the instant stop is pressed, so a
    // provider can't spam-start a second recording during the (multi-second, write-
    // ahead) window before parseRecording flips `transcribing` true. Only for the
    // async transcript pipeline: interactive flows (callback) return their result
    // directly and must not lock the mic. Skip when offline -- the recording is
    // queued and drained later, so there's nothing to show as "transcribing" yet.
    // isServerAvailable, not isOnline: a server outage queues the recording the
    // same way, so arming the lock there would strand the mic on a transcript
    // that isn't running (paired with the audioRecorded gate below).
    if (!callbackRef.current && isServerAvailable) {
      armTranscribeLock(chatIdRef.current);
    }

    if (isMobileApp) {
      stopRecordingApp();
    } else {
      stopRecordingWeb();
    }
  };

  const stopRecordingApp = ({ resumeOnly = false, marker: restoredMarker } = {}) => {
    if (nativeHandoffRef.current) return;
    nativeStartRef.current = null;
    const marker = restoredMarker ||
      peekNativeRecordingMarker() || {
        chatId: chatIdRef.current,
        startedAt: recordStartedAtRef.current,
      };
    const handoff = {
      marker,
      chatId: marker.chatId || chatIdRef.current,
      callback: callbackRef.current,
      transfers: new Set(),
      // Transfers this receiver discarded (chunks that arrived for a session that
      // is not the owner's). A final naming one is declined, never failed.
      abandoned: new Set(),
      receiving: false,
      validated: false,
      disposed: false,
    };
    nativeHandoffRef.current = handoff;

    const releaseHandoff = () => {
      handoff.validated = true;
      handoff.cleanup();
      if (nativeHandoffRef.current === handoff) nativeHandoffRef.current = null;
      if (handoff.marker.recordingUuid) clearNativeRecordingMarker(handoff.marker.recordingUuid);
      if (rescueTimerRef.current) clearTimeout(rescueTimerRef.current);
      rescueTimerRef.current = null;
      setLostRecording(null);
      setLostRecordingState("idle");
    };

    // Nothing can be saved without the owner's session, and the shell must be
    // told: "error" keeps the file on the device and puts it back in the shell's
    // retry queue, which re-offers it through the sync path (usePostMessages'
    // syncRecording, the one that also resolves temp- encounter ids) once the
    // owner signs in. Signed out: custody passes to the phone's queue exactly as
    // the offline branch below does, so the lost-recording guard has nothing
    // left to hold and the clinician gets one honest line instead of a
    // "Recording not saved" prompt on the Login screen. Another user's session:
    // answer the shell but leave the owner's marker and receiver alone.
    const isOwnerMismatch = () => !!marker.userId && marker.userId !== recordingUserRef.current?.id;

    const declineHandoff = (data) => {
      handoff.receiving = false;
      sendRecordingUpdate(data.recordingId, "error");
      dismissRecordingProgressToast(toast, recordingProgressToastId(data.recordingId));
      clearTranscribeLock(handoff.chatId);
      if (!recordingUserRef.current?.id) {
        releaseHandoff();
        toast.info(t("hooks.useRecorder.recordingSavedAfterSignIn"));
        return;
      }
      // The shell has its answer and never re-sends a direct handoff (a retry
      // arrives as syncRecording), so this receiver and its rescue timer are
      // done. Detaching them keeps another user's session from being blocked by,
      // or prompted about, a recording that is not theirs, and frees any
      // buffered chunks. The marker is left alone: it is the owner's, and the
      // resume effect reattaches a receiver for them on sign-in.
      if (rescueTimerRef.current) clearTimeout(rescueTimerRef.current);
      rescueTimerRef.current = null;
      handoff.cleanup();
      if (nativeHandoffRef.current === handoff) nativeHandoffRef.current = null;
    };

    const onAudioRecorded = async (event) => {
      if (handoff.disposed || (event.origin && event.origin !== window.location.origin)) return;
      let message;
      try {
        message = JSON.parse(event.data);
      } catch (_) {
        return;
      }
      const types = ["audioRecorded", "audioRecorded-audioChunkStart", "audioRecorded-audioChunk", "audioRecorded-audioChunkEnd"];
      if (!types.includes(message?.type) || !message.data || typeof message.data !== "object") return;
      const data = message.data;
      const incomingUuid = data.recordingUuid || data.metadata?.recordingUuid;
      if (incomingUuid && marker.recordingUuid && incomingUuid !== marker.recordingUuid) return;
      // The session that started this recording is gone (signed out: the jobs ping
      // logs out on a 403 within seconds, and Stop stays tappable on the Login
      // screen) or belongs to someone else. Chunk payloads are not buffered for
      // it, but the final handoff must still be ANSWERED (declineHandoff):
      // returning silently here is what parked recordings. The shell kept waiting
      // on a webview that had declined custody without saying so, and the
      // lost-recording prompt then steered the clinician to "No recording to
      // recover" for audio still on the device.
      const ownerMismatch = isOwnerMismatch();
      if (ownerMismatch && message.type !== "audioRecorded") {
        // Whatever this transfer carried is now incomplete by the webview's own
        // doing, so a final naming it must be declined even if the owner is back
        // by then (FaceID sign-in is one tap), not failed as "produced no audio".
        if (typeof data.transferId === "string") {
          handoff.abandoned.add(data.transferId);
          delete audioChunkTransfers.current[data.transferId];
        }
        return;
      }
      if (message.type === "audioRecorded") {
        if (!data.recordingId || !data.chatId || (handoff.chatId && data.chatId !== handoff.chatId)) return;
        const transfer = data.transferId && audioChunkTransfers.current[data.transferId];
        if (transfer?.metadata?.recordingId && String(transfer.metadata.recordingId) !== String(data.recordingId)) return;
        if (handoff.receiving) return;
        // Remember the trusted row/UUID pair even if its audio is incomplete.
        // The installed app's manual retry later sends only this native row ID.
        if (incomingUuid && !ownerMismatch) {
          const bound = bindNativeRecordingId(incomingUuid, data.recordingId, recordingUserRef.current?.id);
          if (bound) handoff.marker = bound;
        }
      }
      if (message.deduplicationId) {
        if (lastProcessedMessage.current.has(message.deduplicationId)) return;
        if (lastProcessedMessage.current.size >= MAX_DEDUP_IDS) {
          lastProcessedMessage.current.delete(lastProcessedMessage.current.values().next().value);
        }
        lastProcessedMessage.current.add(message.deduplicationId);
      }

      try {
        if (message.type === "audioRecorded-audioChunkStart") {
          if (typeof data.transferId !== "string" || !data.metadata?.recordingId) return;
          if (handoff.transfers.has(data.transferId)) return;
          handoff.transfers.add(data.transferId);
          audioChunkTransfers.current[data.transferId] = { chunks: {}, receivedChunks: 0, metadata: data.metadata };
          sendRecordingUpdate(data.metadata.recordingId, "parsing");
          return;
        }
        if (message.type === "audioRecorded-audioChunk") {
          if (
            !handoff.transfers.has(data.transferId) ||
            !Number.isInteger(data.chunkIndex) ||
            data.chunkIndex < 0 ||
            typeof data.chunk !== "string"
          )
            return;
          const transfer = audioChunkTransfers.current[data.transferId];
          if (!transfer || Object.prototype.hasOwnProperty.call(transfer.chunks, data.chunkIndex)) return;
          transfer.chunks[data.chunkIndex] = base64ToSingleArrayBuffer(data.chunk);
          transfer.receivedChunks++;
          return;
        }
        if (message.type === "audioRecorded-audioChunkEnd") {
          if (!handoff.transfers.has(data.transferId) || !Number.isInteger(data.totalChunks) || data.totalChunks < 1) return;
          const transfer = audioChunkTransfers.current[data.transferId];
          if (transfer) transfer.totalChunks = data.totalChunks;
          return;
        }

        if (ownerMismatch || handoff.abandoned.has(data.transferId)) return declineHandoff(data);
        handoff.receiving = true;
        let MP3Blob;
        if (data.audio) {
          sendRecordingUpdate(data.recordingId, "parsing");
          MP3Blob = await convertBase64ToMP3(data.audio);
        } else if (data.transferId) {
          const result = processCompletedAudioTransfer(audioChunkTransfers.current, data.transferId);
          if (result) {
            MP3Blob = result.metadata?.isMp3
              ? arrayBuffersToDataUrl(result.buffers, "audio/mpeg")
              : await convertArrayBuffersToMP3(result.buffers);
          }
        }
        if (handoff.disposed) return;
        // Sign-out can also land DURING the conversion above.
        if (isOwnerMismatch()) return declineHandoff(data);
        if (!MP3Blob && !data.textTranscription?.trim()) throw new Error("Recording handoff produced no audio");
        const extraData = data.extraData && JSON.parse(data.extraData);

        // Native has handed this recording over and the payload is usable.
        // Conversion must finish before a later capture can replace any refs.
        // All remaining async save/error work uses this handoff's own identity.
        releaseHandoff();
        if (!nativeSaveRef.current.isServerAvailable) {
          dismissRecordingProgressToast(toast, recordingProgressToastId(data.recordingId));
          sendRecordingUpdate(data.recordingId, "error");
          clearTranscribeLock(handoff.chatId);
          return toast.info(t("hooks.useRecorder.recordingUploadedWhenOnline"));
        }
        linkRecordingProgressToast(data.recordingUuid, data.recordingId);
        // The handed-back audio may be older than the encounter its id now names
        // (ids are recycled after hard deletes), so it is checked like any deferred
        // upload. Interactive (callback) dictations are live and stay unchecked.
        const saveArgs = [
          MP3Blob,
          data.chatId,
          data.phase,
          extraData,
          data.textTranscription,
          data.recordingUuid || data.recordingId,
          handoff.callback,
        ];
        if (!handoff.callback) {
          saveArgs.push(undefined, false, {
            deferred: true,
            sourceChatId: data.chatId,
            startedAt: handoff.marker?.startedAt,
            patientUserId: handoff.marker?.patientUserId,
            encounterUuid: handoff.marker?.encounterUuid,
          });
        }
        const saveOutcome = await nativeSaveRef.current.parseRecording(...saveArgs);
        if (isIdentityRefusal(saveOutcome)) {
          // "error" keeps the audio on the phone; nothing was attached. Remember the
          // refusal: the shell re-offers this recording through the stored-replay path
          // (syncRecording), which must re-judge it with the identity captured here.
          rememberIdentityRefusal(handoff.marker?.userId || recordingUserRef.current?.id, {
            recordingUuid: data.recordingUuid || data.recordingId,
            recordingId: data.recordingId,
            startedAt: handoff.marker?.startedAt,
            patientUserId: handoff.marker?.patientUserId,
            encounterUuid: handoff.marker?.encounterUuid,
            chatId: data.chatId,
          });
          dismissRecordingProgressToast(toast, recordingProgressToastId(data.recordingId));
          sendRecordingUpdate(data.recordingId, "error");
          clearTranscribeLock(handoff.chatId);
          return;
        }
        if (saveOutcome === RECORDING_SAVE_DEFERRED_OFFLINE) {
          dismissRecordingProgressToast(toast, recordingProgressToastId(data.recordingId));
          sendRecordingUpdate(data.recordingId, "error");
          clearTranscribeLock(handoff.chatId);
        }
      } catch (error) {
        // An unrelated or malformed chunk cannot retire a recording. A failed
        // complete payload remains guarded until its UUID is saved by recovery.
        if (handoff.disposed && !handoff.validated) return;
        if (message.type !== "audioRecorded") return;
        if (!error?.serverUnavailable) toast.error(t("hooks.useRecorder.errorSavingRecording"));
        dismissRecordingProgressToast(toast, recordingProgressToastId(data.recordingId));
        sendRecordingUpdate(data.recordingId, "error");
        clearTranscribeLock(handoff.chatId);
        if (!handoff.validated) {
          handoff.receiving = false;
          handoff.timedOut = true;
          setLostRecording(handoff.marker);
          setLostRecordingState("notRecovered");
        }
      }
    };

    handoff.cleanup = () => {
      handoff.disposed = true;
      document.removeEventListener("message", onAudioRecorded);
      window.removeEventListener("message", onAudioRecorded);
      for (const transferId of handoff.transfers) delete audioChunkTransfers.current[transferId];
    };
    window.addEventListener("message", onAudioRecorded);
    document.addEventListener("message", onAudioRecorded);

    if (!resumeOnly && !marker.stoppedAt) {
      markNativeRecordingMarkerStopped();
      handoff.marker = peekNativeRecordingMarker() || marker;
      sendStopRecording();
    }
    if (rescueTimerRef.current) clearTimeout(rescueTimerRef.current);
    const stoppedAt = handoff.marker.stoppedAt || Date.now();
    const remaining = Math.max(0, TRANSCRIBE_LOCK_BACKSTOP_MS - (Date.now() - stoppedAt));
    handoff.timedOut = !!handoff.marker.recoveryCandidate || remaining === 0;
    // Old markers protect explicit starts without reopening a days-old prompt
    // merely because this page mounted. Keep listening for their handoff.
    if (!resumeOnly || readNativeRecordingMarker(marker.userId, { ignoreGrace: true })) {
      rescueTimerRef.current = setTimeout(() => {
        rescueTimerRef.current = null;
        if (nativeHandoffRef.current !== handoff) return;
        handoff.timedOut = true;
        setLostRecording(handoff.marker);
        setLostRecordingState("notRecovered");
      }, remaining);
    }

    setRecording(false);
    setStartingRecording(false);
    setRecordingElapsed(0);
  };

  const stopRecordingWeb = () => {
    setRecording(false);
    setStartingRecording(false);
    setRecordingElapsed(0);
    const session = webSessionRef.current;
    if (!session) return;
    if (!session.started) {
      releaseWebSession(session, true);
      return;
    }
    if (session.finishing) return;
    session.finishing = true;
    session.ownership.update("finalizing");
    cleanupAudioAnalysis();
    if (activeCaptureRef.current && activeCaptureRef.current.active) {
      // Finalize the streamed recording: last segment marked final + assembled.
      finalizeSegmentedCapture();
      return;
    }
    if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") mediaRecorder.current.stop();
    streamRef.current?.getTracks().forEach(function (track) {
      track.stop();
    });
    streamRef.current = null;
  };

  // Recomputed every render; the 1s time ticks guarantee a render lands right
  // after the threshold passes. True whenever not recording so non-recording
  // consumers never see a disabled control.
  const canStopRecording =
    !recording || !recordStartedAtRef.current || Date.now() - recordStartedAtRef.current >= MIN_RECORDING_MS;

  return {
    recording,
    startRecording,
    stopRecording,
    setRecording,
    canStopRecording,
    time,
    recordingElapsed,
    ruralMode,
    quickstart,
    recordingVariable,
    chatIdRef,
    startingRecording,
    devices,
    currentDevice,
    setCurrentDevice,
    volumeLevel,
    showNoAudioWarning,
    showModalRecordingPermission,
    setShowModalRecordingPermission,
    showModalHealthCheck,
    closeHealthCheckAndContinue,
    guardRecordingStart,
    lostRecording,
    lostRecordingState,
    attemptLostRecordingRescue,
    dismissLostRecording,
    confirmLostRecordingRecovered,
    confirmNativeStartFailed,
    confirmNoRecordingToRecover,
    stampLocalRecordingsChecked,
    hasLocalRecordingsEvidence,
    webRecordingRecovery:
      webRecordingRecovery &&
      webRecordingRecovery.journal.userId === auth?.user?.id &&
      webRecordingRecovery.journal.orgId === currentOrgId()
        ? webRecordingRecovery
        : null,
    dismissWebRecordingRecovery,
    reviewWebRecordingRecovery,
    continueAfterWebRecordingReview,
    retryWebRecordingSave,
  };
}

export function ProvideRecorder({ children }) {
  const values = useRecorderProvider();
  return <RecorderContext.Provider value={values}>{children}</RecorderContext.Provider>;
}

export const useRecorder = () => {
  return useContext(RecorderContext);
};
